import React, { useState, useEffect, useMemo, useCallback } from "react";
import { DndProvider } from "react-dnd";
import Loader from "react-spinner-loader";
import { HTML5Backend } from "react-dnd-html5-backend";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import "react-datepicker/dist/react-datepicker.css";
import Select from "react-select";
import "./NewDashboard.css";
import {
  PlusCircle,
  Edit2,
  X,
  Save,
  BarChart3,
  CreditCard,
  TrendingUp,
  Users,
  Building,
  MapPin,
  AlertTriangle,
  Shield,
  Search,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Camera,
  Trash,
  ArrowLeft,
  FileText,
  SaveAll,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import "leaflet-control-geocoder";
import "leaflet-control-geocoder/dist/Control.Geocoder.css";
import { getData, postDataWithBody } from "../../../Services/Services";
import {
  createDashboardForApi,
  findRegionsByUser,
  getallBanks,
  getblocksfromdistid,
  getInstanceUrl,
  getQueriesListByWidgetTypeForAPI,
  getThemeURL,
  getUserlistByBank,
} from "../../../Services/EndPoints";
import { toast } from "react-toastify";
import GridLayoutCards from "./GridLayoutCards";
import { DraggableCard } from "./DraggableCard";
import MapEdit from "./EditWidget/MapEditPopUp";
import CardEditPopup from "./EditWidget/CardEditPopup";
import TableEditForm from "./EditWidget/TableEditPopup";
import ChartEditPopUp from "./EditWidget/ChartEditPopUp";
import TreeListEditPopup from "./EditWidget/HOBankListEditPopup";

// Fix for default marker icons in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

const chartColors = [
  "#1a7a73", // blue-500
  "#10B981", // emerald-500
  "#8B5CF6", // violet-500
  "#EF4444", // red-500
  "#F59E0B", // amber-500
  "#EC4899", // pink-500
  "#14B8A6", // teal-500
  "#84CC16", // lime-500
];

const iconMap = {
  "Atm's": new L.Icon({
    iconUrl: "https://cdn-icons-png.flaticon.com/512/10410/10410410.png",
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  }),
  "Bank's": new L.Icon({
    iconUrl: "https://cdn-icons-png.flaticon.com/512/10410/10410388.png",
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  }),
  Incidents: new L.Icon({
    iconUrl: "https://cdn-icons-png.flaticon.com/512/564/564619.png",
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  }),
  "Cash vans": new L.Icon({
    iconUrl: "https://cdn-icons-png.flaticon.com/512/854/854894.png",
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  }),
  default: new L.Icon.Default(),
};

const CreateNewDashboardForApi = () => {
  const location = useLocation();
  const [loader, setLoader] = useState(false);
  const navigate = useNavigate();
  const isViewMode = location.state?.isViewing;
  const isEditMode = location.state?.isEdit;
  const [dataSourcesForTable, setDataSourcesForTable] = useState({});
  // Add this with your existing state variables
  const [defaultBankId, setDefaultBankId] = useState(null);
  const [dataSourcesForMap, setDataSourcesForMap] = useState({});
  const [tablePagination, setTablePagination] = useState({});
  const [uploadedIcons, setUploadedIcons] = useState({});
  const [iconSizes, setIconSizes] = useState({});
  const [dashboardStatus, setDashboardStatus] = useState("Draft");
  const [districtList, setDistrictList] = useState([]);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [blockList, setBlockList] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [userRegionName, setUserRegionName] = useState("");
  const [userlist, setUserlist] = useState([]);
  const [popupContext, setPopupContext] = useState(null); // 'create' or 'addWidget'
  const [dateRange, setDateRange] = useState({
    from: null,
    to: null,
  });
  const [newItem, setNewItem] = useState({
    type: "card",
    title: "",
    value: "",
    subtitle: "",
    color: "#ffffff",
    icon: "building",
    chartType: "line",
    data: [],
    xAxisKey: "",
    yAxisKeys: [],
    colors: ["#ffffff", "#10B981"],
    columns: [],
    showDownload: false,
    showDownloadExcelChart: false,
    showDownloadPDFChart: false,
    showBankLogo: false,
    showPDFDownload: false,
    showSearch: false,
    showPagination: false,
    headerColor: "#f9fafb",
    headerTextColor: "#374151",
    subHeaderColor: "#f1f5f9",
    subHeaderTextColor: "#334155",
    showDateFilter: false,
    yAxisLabels: [],
    pieColors: [],
    xAxisLabelFontSize: 14,
    yAxisLabelFontSize: 14,
    legendFontSize: 14,
    title_color: "#000000",
    title_fontSize: 16,
    title_textAlignment: "left",
  });
  const [savedQueries, setSavedQueries] = useState([]);
  const [SavedAllQueries, setSavedAllQueries] = useState([]);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [editMode, setEditMode] = useState("widget"); // 'dashboard' or 'widget'
  const [dashboardFormErrors, setDashboardFormErrors] = useState({});
  const [DistrictId, setDistrictId] = useState();
  const [bankList, setBankList] = useState([]);
  const [newItemForDate, setNewItemForDate] = useState(null); // 'create' or 'addWidget'

  const getCurrentMode = () => {
    if (location.state?.isViewing) return "View";
    if (location.state?.isEdit) return "Edit";
    if (location.state?.mode === "create" || location.state?.fromCreatePage)
      return "Create";
    return "Create";
  };

  // Inside CreateNewDashboard component, add this function:
  const handleDateFieldsUpdate = (dateFields) => {
    console.log("Received date fields from table widget:", dateFields);

    // Store date fields for this widget
    setNewItemForDate(dateFields);

    // You can also store it globally if needed for other widgets
    // setAvailableDateFields(dateFields);
  };

  const formatChartTypeName = (chartType) => {
    // Handle specific cases first
    const specialCases = {
      progressBar: "Progress Bar",
      radialProgress: "Radial Progress",
      stackedBar: "Stacked Bar",
    };

    if (specialCases[chartType]) {
      return specialCases[chartType];
    }

    // For other camelCase names, add spaces before capital letters
    return chartType
      .replace(/([A-Z])/g, " $1") // Add space before capital letters
      .replace(/^./, (str) => str.toUpperCase()) // Capitalize first letter
      .trim();
  };

  const currentMode = getCurrentMode();

  const isFormValid = () => {
    // Check for any date validation errors across all widget types
    if (
      newItem.dateValidationError ||
      newItem.toDateValidationError ||
      newItem.chartDateValidationError ||
      newItem.tableDateValidationError ||
      newItem.tableToDateValidationError ||
      newItem.mapDateValidationError ||
      newItem.mapToDateValidationError
    ) {
      return false;
    }

    // Check if fromDate is selected but toDate is not (applies to all widget types)
    if (newItem.fromDate && !newItem.toDate) {
      return false;
    }

    // Widget-specific validation
    if (newItem.type === "chart") {
      // Check if dates are selected but filter hasn't been applied
      const hasPendingDateFilter =
        newItem.fromDate &&
        newItem.toDate &&
        newItem.dateFieldsChanged &&
        !newItem.dateFilterApplied;
      return (
        newItem.dataSource &&
        newItem.yAxisKeys.length > 0 &&
        newItem.yAxisKeys.every((key) => key) &&
        !hasPendingDateFilter &&
        (!newItem.fromDate || (newItem.fromDate && newItem.toDate))
      );
    } else if (newItem.type === "table") {
      // Table-specific validation with date filter check
      const hasValidDataSource =
        newItem.dataSource && newItem.dataSource.trim() !== "";
      const hasValidTitle = newItem.title.trim() !== "";

      // Check if dates are selected but filter hasn't been applied
      const hasPendingDateFilter =
        newItem.fromDate &&
        newItem.toDate &&
        newItem.dateFieldsChanged &&
        !newItem.dateFilterApplied;

      return (
        hasValidDataSource &&
        hasValidTitle &&
        !hasPendingDateFilter &&
        (!newItem.fromDate || (newItem.fromDate && newItem.toDate))
      );
    } else if (newItem.type === "card") {
      const hasPendingDateFilter =
        newItem.fromDate &&
        newItem.toDate &&
        newItem.dateFieldsChanged &&
        !newItem.dateFilterApplied;
      return (
        newItem.dataSource &&
        newItem.dataSource.trim() !== "" &&
        newItem.title.trim() !== "" &&
        !hasPendingDateFilter &&
        (!newItem.fromDate || (newItem.fromDate && newItem.toDate))
      );
    } else if (newItem.type === "map") {
      // Check if dates are selected but filter hasn't been applied
      const hasPendingDateFilter =
        newItem.fromDate &&
        newItem.toDate &&
        newItem.dateFieldsChanged &&
        !newItem.dateFilterApplied;
      return (
        newItem.dataSource &&
        Array.isArray(newItem.dataSource) &&
        newItem.dataSource.length > 0 &&
        newItem.title.trim() !== "" &&
        !hasPendingDateFilter &&
        (!newItem.fromDate || (newItem.fromDate && newItem.toDate))
      );
    }
    return newItem.title.trim() !== "";
  };

  const isDisabled = !isFormValid() || !newItem.title;

  const xAxisOrientationOptions = [
    { value: "horizontal", label: "Horizontal (0°)" },
    { value: "angled", label: "Angled (-30°)" },
    { value: "vertical", label: "Vertical (-90°)" },
  ];
  const validateDashboardStepOne = () => {
    let errors = {};

    // Add title validation
    if (!dashboardConfig.title || dashboardConfig.title.trim() === "") {
      errors.title = "Dashboard title is required.";
    }

    // Enhanced bank validation for SUPER ADMIN
    if (userRole === "SUPER ADMIN") {
      if (
        !dashboardConfig.selectedBankIds ||
        dashboardConfig.selectedBankIds.length === 0 ||
        (dashboardConfig.selectedBankIds.length === 1 &&
          dashboardConfig.selectedBankIds[0] === "")
      ) {
        errors.selectedBankIds = "Please select at least one Bank.";
      }

      // UPDATED: Validate default bank selection when banks are selected
      // Remove the condition that excludes "all" - require default bank in ALL cases
      if (
        dashboardConfig.selectedBankIds &&
        dashboardConfig.selectedBankIds.length > 0
      ) {
        if (
          !dashboardConfig.defaultBankId ||
          dashboardConfig.defaultBankId === ""
        ) {
          errors.defaultBankId = "Please select a default bank.";
        }
      }

      // Clear bank selection error if "all" is selected (but keep default bank requirement)
      if (dashboardConfig.selectedBankIds?.includes("all")) {
        delete errors.selectedBankIds;
      }
    }

    setDashboardFormErrors(errors);
    // True if there are no errors
    return Object.keys(errors).length === 0;
  };

  useEffect(() => {
    // This will run validation whenever form inputs change
  }, [newItem.dataSource, newItem.yAxisKeys, newItem.title, newItem.type]);

  // useEffect(() => {
  //   const fetchCardData = async () => {
  //     // Only execute if the condition is met
  //     if (newItem.type !== "card" || newItem.dataSource) {
  //       return; // Exit early if not card or data already fetched
  //     }

  //     // Check if we have saved queries and the popup is open
  //     if (savedQueries.length === 0 || !showEditPopup) {
  //       return;
  //     }

  //     // Find the first available service that works for cards
  //     const cardCompatibleQuery = savedQueries.find(
  //       (q) => q.serviceName && q.queryBuilder
  //     );

  //     if (!cardCompatibleQuery) {
  //       console.log("No compatible query found for card widget");
  //       return;
  //     }

  //     const serviceName = cardCompatibleQuery.serviceName;
  //     setLoader(true);

  //     try {
  //       const queryObject =
  //         typeof cardCompatibleQuery.queryBuilder === "string"
  //           ? JSON.parse(cardCompatibleQuery.queryBuilder)
  //           : cardCompatibleQuery.queryBuilder;

  //       // Add any necessary filters here
  //       const filters = {};

  //       // You might want to add user-specific filters here similar to HO_bank_list
  //       try {
  //         const userResponse = await getData(getThemeURL);
  //         if (
  //           userResponse &&
  //           Array.isArray(userResponse) &&
  //           userResponse.length > 0
  //         ) {
  //           const user = userResponse[0];
  //           // Add user-based filtering if needed
  //           if (user.branch_id) {
  //             filters.branch_id = user.branch_id;
  //           }
  //           if (user.bank_id) {
  //             filters.bank_id = user.bank_id;
  //           }
  //         }
  //       } catch (error) {
  //         console.error("Error fetching user profile for card filters:", error);
  //       }

  //       const enhancedQuery = {
  //         ...queryObject,
  //         filters: {
  //           ...queryObject.filters,
  //           ...filters,
  //         },
  //       };

  //       console.log("Card Query sent to /nifi/fetchQuery:", enhancedQuery);

  //       const response = await fetch("/nifi/fetchQuery", {
  //         method: "POST",
  //         headers: {
  //           "Content-Type": "application/json",
  //         },
  //         body: JSON.stringify(enhancedQuery),
  //       });

  //       const result = await response.json();
  //       console.log("Card Data - Auto Fetch Result:", result);

  //       if (result.data && result.data.length > 0) {
  //         // Process the data for card widget
  //         const availableFields = Object.keys(result.data[0] || {});

  //         // Create default field configurations
  //         const fieldConfigs = availableFields.map((field) => ({
  //           fieldName: field,
  //           color: "#000000",
  //           fontSize: "medium",
  //           fontSizeValue: 14,
  //           textAlignment: "left",
  //           enabled: true,
  //         }));

  //         // Update newItem with the fetched data
  //         setNewItem({
  //           ...newItem,
  //           dataSource: serviceName,
  //           data: result.data,
  //           fieldConfigs: fieldConfigs,
  //           allAvailableFields: availableFields,
  //           dataSourceConfig: {
  //             data: result.data,
  //             columns: result.columns || availableFields,
  //             serviceName: serviceName,
  //             availableFields: availableFields,
  //             originalData: result.data,
  //           },
  //         });
  //       } else {
  //         console.log("Card - No data returned");
  //         setNewItem({
  //           ...newItem,
  //           dataSource: serviceName,
  //           data: [],
  //           fieldConfigs: [],
  //           allAvailableFields: [],
  //         });
  //       }
  //     } catch (error) {
  //       console.error("Error fetching card data:", error);
  //       setNewItem({
  //         ...newItem,
  //         dataSource: serviceName || "",
  //         data: [],
  //         fieldConfigs: [],
  //         allAvailableFields: [],
  //       });
  //     } finally {
  //       setLoader(false);
  //     }
  //   };

  //   fetchCardData();
  // }, [newItem.type, savedQueries, newItem.dataSource]); // Added showEditPopup to dependencies

  useEffect(() => {
    const fetchSavedQueries = async () => {
      try {
        // setLoadingQueries(true);
        // setQueryError(null);

        // Determine widget type based on newItem.type
        let widgetType = "";
        switch (newItem.type) {
          case "card":
            widgetType = "CARD";
            break;
          case "chart":
            widgetType = "CHART";
            break;
          case "table":
            widgetType = "TABLE";
            break;
          case "map":
            widgetType = "MAP";
            break;
          case "HO_bank_list":
            widgetType = "TREE_LIST";
            break;
          default:
            widgetType = "CARD"; // Default fallback
        }

        const response = await getData(
          getQueriesListByWidgetTypeForAPI.replace("WIDGETTYPE", widgetType)
        );

        console.log("WIDGETTYPEresponse", response);

        const Newparsed = response.map((item) => ({
          ...item,
          queryBuilder:
            typeof item.queryBuilder === "string"
              ? JSON.parse(item.queryBuilder)
              : item.queryBuilder,
        }));
        setSavedAllQueries(Newparsed);

        // Parse + filter only PRIMARY queries
        const parsed = response
          .map((item) => ({
            ...item,
            queryBuilder:
              typeof item.queryBuilder === "string"
                ? JSON.parse(item.queryBuilder)
                : item.queryBuilder,
          }))
          .filter((item) => item.queryStage === "PRIMARY"); // <-- only keep PRIMARY

        setSavedQueries(parsed);
      } catch (err) {
        console.error("Error fetching saved queries:", err);
        // setQueryError("Failed to load saved queries");
      } finally {
        // setLoadingQueries(false);
      }
    };

    fetchSavedQueries();
  }, [newItem.type]);

  const handleYAxisLabelChange = (index, value) => {
    const updatedItem = { ...newItem };
    updatedItem.yAxisLabels = updatedItem.yAxisLabels || [];
    updatedItem.yAxisLabels[index] = value;
    setNewItem(updatedItem);
  };
  const renderIcon = (iconType) => {
    const iconProps = { size: 24 };
    switch (iconType) {
      case "building":
        return <Building {...iconProps} />;
      case "credit-card":
        return <CreditCard {...iconProps} />;
      case "users":
        return <Users {...iconProps} />;
      case "trending-up":
        return <TrendingUp {...iconProps} />;
      case "map-pin":
        return <MapPin {...iconProps} />;
      case "alert-triangle":
        return <AlertTriangle {...iconProps} />;
      case "shield":
        return <Shield {...iconProps} />;
      default:
        return <BarChart3 {...iconProps} />;
    }
  };

  const handleTablePageChange = (itemId, newPage) => {
    setTablePagination((prev) => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        page: newPage,
      },
    }));
  };

  const handleTableRowsPerPageChange = (itemId, newRowsPerPage) => {
    setTablePagination((prev) => ({
      ...prev,
      [itemId]: {
        page: 1, // Reset to first page when changing rows per page
        rowsPerPage: newRowsPerPage,
      },
    }));
  };

  const filterDataByDateRange = (data, dateKey, fromDate, toDate) => {
    if (!fromDate && !toDate) return data;

    return data.filter((item) => {
      try {
        const itemDateStr = item[dateKey];
        if (!itemDateStr) return false;

        // Parse the item date (handle both created_on and month names)
        let itemDate;
        if (dateKey === "month") {
          // Handle month names as before
          const monthMap = {
            Jan: "01",
            Feb: "02",
            Mar: "03",
            Apr: "04",
            May: "05",
            Jun: "06",
            Jul: "07",
            Aug: "08",
            Sep: "09",
            Oct: "10",
            Nov: "11",
            Dec: "12",
          };
          const year = item.created_on ? item.created_on.split("-")[0] : "2025"; // Use created_on for year
          const month = monthMap[itemDateStr];
          itemDate = new Date(`${year}-${month}-01`);
        } else {
          itemDate = new Date(itemDateStr);
        }

        if (isNaN(itemDate.getTime())) return false;

        const from = fromDate ? new Date(fromDate) : null;
        const to = toDate ? new Date(toDate) : null;

        if (from && to) {
          return itemDate >= from && itemDate <= to;
        } else if (from) {
          return itemDate >= from;
        } else if (to) {
          return itemDate <= to;
        }
        return true;
      } catch (e) {
        console.error("Error filtering by date:", e);
        return false;
      }
    });
  };

  useEffect(() => {
    const loadDataSources = async () => {
      try {
        const sources = {};
        setDataSourcesForTable(sources);
      } catch (error) {
        console.error("Error loading data sources:", error);
      }
    };
    loadDataSources();
  }, []);
  useEffect(() => {
    const loadDataSourcesForMap = async () => {
      try {
        const sources = {};
        setDataSourcesForMap(sources);
      } catch (error) {
        console.error("Error loading data sources:", error);
      }
    };
    loadDataSourcesForMap();
  }, []);
  const handleIconUpload = (source, file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const newIcons = {
          ...uploadedIcons,
          [source]: e.target.result,
        };
        setUploadedIcons(newIcons);
        resolve(e.target.result);
      };
      reader.readAsDataURL(file);
    });
  };

  const parentRegionId =
    location.state?.parentregionid ||
    location.state?.dashboardData?.parentregionid ||
    ""; // Fallback

  // 2. Find the district that matches the parentRegionId
  const matchingDistrict =
    districtList.find(
      (district) => String(district.id) === String(parentRegionId)
    ) || null;

  // 3. Get the district name
  const matchingDistrictName = matchingDistrict ? matchingDistrict.value : "";

  console.log(matchingDistrict, "debugging the district");

  const initializeDashboard = useCallback(() => {
    // If we're viewing an existing dashboard, use its data
    if (location.state?.dashboardData) {
      let selectedUserIds = [];
      let selectedDistricts = [];
      let selectedBankIds = [];
      let defaultBankIdFromData = null;

      // Handle different data formats for selectedUserIds
      if (location.state.dashboardData.userId) {
        if (Array.isArray(location.state.dashboardData.userId)) {
          selectedUserIds = location.state.dashboardData.userId;
        } else if (typeof location.state.dashboardData.userId === "string") {
          selectedUserIds = location.state.dashboardData.userId
            .split(",")
            .filter(Boolean)
            .map((id) => id.trim());
        } else {
          selectedUserIds = [String(location.state.dashboardData.userId)];
        }
      }

      // Handle multiple districts from parentregionid
      if (location.state.dashboardData.parentregionid) {
        if (Array.isArray(location.state.dashboardData.parentregionid)) {
          selectedDistricts = location.state.dashboardData.parentregionid.map(
            (id) => String(id)
          );
        } else if (
          typeof location.state.dashboardData.parentregionid === "string"
        ) {
          selectedDistricts = location.state.dashboardData.parentregionid
            .split(",")
            .filter(Boolean)
            .map((id) => id.trim());
        } else {
          selectedDistricts = [
            String(location.state.dashboardData.parentregionid),
          ];
        }
      }
      if (location.state.dashboardData.bankids) {
        selectedBankIds = String(location.state.dashboardData.bankids)
          .split(",")
          .filter(Boolean)
          .map((id) => id.trim());

        // If no specific banks are selected, it means "All Banks" was chosen
        if (selectedBankIds.length === 0) {
          selectedBankIds = [];
        }
      }

      if (location.state.dashboardData.defaultBankId) {
        if (typeof location.state.dashboardData.defaultBankId === "string") {
          defaultBankIdFromData =
            location.state.dashboardData.defaultBankId.trim();
        } else {
          defaultBankIdFromData = String(
            location.state.dashboardData.defaultBankId
          );
        }
      }

      let globalFilters = {
        search: true,
        dateRange: true,
        status: false,
        category: false,
        customFilters: [],
      };

      // Check if globalFiltersBuilder exists and parse it
      // if (location.state.dashboardData.globalFiltersBuilder) {
      try {
        const parsedFilters =
          typeof location.state.dashboardData.globalfillters === "string"
            ? JSON.parse(location.state.dashboardData.globalfillters)
            : location.state.dashboardData.globalfillters;

        console.log(parsedFilters, "parssssssssssssssssssssss");

        globalFilters = {
          ...globalFilters,
          ...parsedFilters,
        };
      } catch (error) {
        console.error("Error parsing global filters:", error);
        // Keep default values if parsing fails
      }
      // }

      console.log("Parsed global filters:", globalFilters); // Debug log

      console.log("edit configuration date", location.state);
      return {
        items: location.state.dashboardData.layout || [],
        name: location.state.dashboardData.title || "",
        description: location.state.dashboardData.description || "",
        selectedDistrict: DistrictId || "",
        selectedDistricts: selectedDistricts, // New multi-select field
        selectedBankIds: selectedBankIds,
        selectedUserIds: selectedUserIds,
        filters: globalFilters,
        permissions: location.state.dashboardData.permissions || {
          viewers: ["All Users", "Branch Managers", "Executives"],
          editors: ["Admin", "Dashboard Manager"],
          selectedViewers: ["All Users"],
          selectedEditors: ["Admin"],
        },
        attachmentList: location.state.dashboardData.attachmentList,
        defaultBankId: defaultBankIdFromData,
      };
    }

    // Return defaults for new dashboard
    return {
      items: [],
      name: "",
      description: "",
      selectedDistrict: "",
      selectedDistricts: [], // New multi-select field
      selectedBankIds: [],
      selectedUserIds: [],
      filters: {
        search: true,
        dateRange: true,
        status: false,
        category: false,
        customFilters: [],
      },
      permissions: {
        viewers: ["All Users", "Branch Managers", "Executives"],
        editors: ["Admin", "Dashboard Manager"],
        selectedViewers: ["All Users"],
        selectedEditors: ["Admin"],
        defaultBankId: null,
      },
    };
  }, [location.state]);

  const initializedData = initializeDashboard();
  console.log("initializedData", initializedData);
  const [dashboardItems, setDashboardItems] = useState(initializedData.items);
  const [dashboardName, setDashboardName] = useState(
    initializedData.name || ""
  );
  const [dashboardDescription, setDashboardDescription] = useState(
    initializedData.description
  );
  const [removedItems, setRemovedItems] = useState([]);
  const [isEditingLayout, setIsEditingLayout] = useState(false);
  const [showRemovedSection, setShowRemovedSection] = useState(false);
  // Add these to your existing state variables
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [gridView, setGridView] = useState("grid");
  const [searchTerm, setSearchTerm] = useState("");
  const [userId, setUserId] = useState(null);
  const [roleName, setRoleName] = useState(null);
  const [showPopup, setShowPopup] = useState(
    location.state?.fromCreatePage || false
  );
  const [fieldOptions, setFieldOptions] = useState([]);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [dashboardConfig, setDashboardConfig] = useState(() => {
    const initialData = initializeDashboard();
    return {
      title: initialData.name || "",
      description: initialData.description,
      selectedDistrict: initialData.selectedDistrict,
      selectedDistricts: initialData.selectedDistricts || [],
      selectedBankIds: initialData.selectedBankIds || [],
      selectedUserIds: initialData.selectedUserIds,
      globalFilters: initialData.filters,
      permissions: initialData.permissions,
      defaultBankId: initialData.defaultBankId,
    };
  });
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isSavingDashboard, setIsSavingDashboard] = useState(false);
  const [attachmentList, setAttachmentList] = useState(
    initializedData.attachmentList
  );

  console.log(location.state, "globalfillterslocation");

  const saveDashboard = async (status, setLoadingState) => {
    setDashboardStatus(status);
    console.log(dashboardStatus, "dashboardStatus in saveDashboard");

    try {
      setLoadingState(true); // Use the passed loading state setter
      await saveDashboardToDatabase(status);
      return true;
    } catch (error) {
      console.error("Failed to save dashboard:", error);
      toast.error("Failed to save dashboard. Please try again.", {
        position: "top-center",
      });
      return false;
    } finally {
      setLoadingState(false); // Reset the specific loading state
    }
  };

  const saveDashboardToDatabase = useCallback(
    async (status = dashboardStatus || "Draft") => {
      const dashboardId = location.state?.dashboardId || `dash_${Date.now()}`;

      // Fetch user ID before saving
      let fetchedUserId = null;
      let adminRole = "U";
      let userRoleFromAPI = null;
      let userRoleFromAPIForHO = null;
      let userRegionId = null;
      let userBankId = null;
      try {
        const response = await getData(getThemeURL);
        console.log(response, "User profile response");
        if (response && Array.isArray(response) && response.length > 0) {
          fetchedUserId = response[0].id;
          const roleName = response[0].role_name?.toUpperCase();
          userRoleFromAPI = roleName;
          userRoleFromAPIForHO = response[0].is_admin;
          userRegionId = response[0].region_id;
          userBankId = response[0].bank_id;
          console.log("Role name:", roleName);

          if (roleName === "SUPER ADMIN") {
            adminRole = "SA";
          } else if (response[0].is_admin === true) {
            adminRole = "HO";
          } else if (roleName === "BRANCH OFFICER") {
            adminRole = "BA"; // Add Branch Officer mapping
          } else {
            adminRole = "U";
          }
          console.log("Mapped admin role:", adminRole);
        }
      } catch (error) {
        console.error("Error fetching user profile:", error);
        toast.error("Failed to fetch user profile.", {
          position: "top-center",
        });
        // setIsSavingDashboard(false);
        return;
      }

      if (!fetchedUserId) {
        toast.error("User ID is not available. Cannot save dashboard.", {
          position: "top-center",
        });
        // setIsSavingDashboard(false);
        return;
      }

      // Determine bankIds based on user role
      let bankIdsToSave;
      if (userRoleFromAPIForHO === true) {
        // For HO users, use their specific bank ID
        bankIdsToSave = userBankId ? String(userBankId) : "";
      } else {
        // For other users, use the selected bank IDs
        bankIdsToSave = dashboardConfig.selectedBankIds
          .filter((id) => id !== "all")
          .join(",");
      }

      console.log(status, "dashboardStatus in saveDashboardToDatabase");
      console.log(
        "Bank IDs to save:",
        bankIdsToSave,
        "for role:",
        userRoleFromAPI
      );

      const dashboardData = {
        title: dashboardName,
        description: dashboardDescription,
        status: status, // Use the parameter instead of the state variable
        views: 0,
        created_by: fetchedUserId,
        adminRole: adminRole,
        bankIds: bankIdsToSave,
        regionId:
          userRoleFromAPI === "SUPER ADMIN"
            ? 52
            : Number(dashboardConfig.selectedDistrict),
        layoutBuilder: JSON.stringify({ layout: dashboardItems }),
        globalFiltersBuilder: JSON.stringify(
          dashboardConfig.globalFilters || {}
        ),
        permissionsBuilder: JSON.stringify(dashboardConfig.permissions || {}),
        attachmentList: attachmentList,
        defaultBankId:
          userRoleFromAPI === "SUPER ADMIN"
            ? dashboardConfig.defaultBankId
            : userRoleFromAPIForHO === true || userRoleFromAPI === false
            ? userBankId
            : dashboardConfig.defaultBankId,
      };

      if (userRoleFromAPIForHO === true && userRegionId) {
        dashboardData.parentRegionId = String(userRegionId);
        console.log(
          "Added parentRegionId for HO user:",
          dashboardData.parentRegionId
        );
      }

      if (userRoleFromAPI === "SUPER ADMIN") {
        // Determine the parentRegionId to use - now supporting multiple districts
        let parentRegionIdToSend;

        if (
          dashboardConfig.selectedDistricts &&
          dashboardConfig.selectedDistricts.length > 0
        ) {
          // Filter out "all" if it exists and use only the actual district IDs
          const filteredDistricts = dashboardConfig.selectedDistricts.filter(
            (id) => id !== "all"
          );
          parentRegionIdToSend = filteredDistricts.join(",");
        } else if (DistrictId) {
          // If user selected a single district (backward compatibility), use the selected one
          parentRegionIdToSend = DistrictId.split(",")
            .filter((id) => id !== "all")
            .join(",");
        } else if (location.state?.dashboardData?.parentregionid) {
          // If no new selection but we have existing data, use the existing one
          const existing = location.state.dashboardData.parentregionid;
          if (Array.isArray(existing)) {
            parentRegionIdToSend = existing
              .filter((id) => id !== "all")
              .join(",");
          } else {
            parentRegionIdToSend = String(existing)
              .split(",")
              .filter((id) => id !== "all")
              .join(",");
          }
        } else if (dashboardConfig.selectedDistrict) {
          // Fallback to single district selection
          parentRegionIdToSend = String(dashboardConfig.selectedDistrict);
        }

        // Always include parentRegionId if we have a value
        if (parentRegionIdToSend) {
          dashboardData.parentRegionId = parentRegionIdToSend;
        }

        console.log("Final parentRegionId being sent:", parentRegionIdToSend);
      }

      if (isEditMode) {
        dashboardData.id = dashboardId;
      }
      console.log("attachement api", attachmentList);
      try {
        const response = await postDataWithBody(
          createDashboardForApi,
          dashboardData,
          {}
        );
        if (status === "Draft") {
          toast.success("Dashboard drafted successfully!", {
            position: "top-center",
          });
        } else {
          toast.success("Dashboard saved successfully!", {
            position: "top-center",
          });
        }
        navigate("../dashboardbuilderForApi");
        return response;
      } catch (error) {
        console.error("Error saving dashboard:", error);
        toast.error("Failed to save dashboard.", { position: "top-center" });
        throw error;
      } finally {
        setIsSavingDashboard(false);
      }
    },
    [
      dashboardItems,
      dashboardName,
      dashboardDescription,
      dashboardConfig,
      defaultBankId,
      dashboardStatus, // Keep this in dependencies
      location.state?.dashboardId,
      isEditMode,
      navigate,
      districtList,
    ]
  );
  const fetchDistrictList = async () => {
    setLoadingDistricts(true);
    try {
      const response = await getData(findRegionsByUser);
      if (response && Array.isArray(response)) {
        setDistrictList(response);

        // If user is Head Office Admin and there's a district, fetch blocks
        if (roleName && roleName.length > 0) {
          const role = roleName[0]?.role_name?.toUpperCase();
          const roleForHO = roleName[0]?.is_admin;
          setUserRole(role);

          if (roleForHO === true && response.length > 0) {
            // Automatically fetch blocks for the first district or all districts
            await fetchBlocksForDistrict(response[0].id);
          }
        }
      } else {
        console.error("Invalid districts data format:", response);
        toast.error("Failed to load districts data");
      }
    } catch (error) {
      console.error("Error fetching districts:", error);
      toast.error("Failed to load districts data");
    } finally {
      setLoadingDistricts(false);
    }
  };

  const fetchBanks = async () => {
    try {
      const response = await getData(getallBanks);
      console.log("Banks data", response);
      if (response && Array.isArray(response)) {
        const formattedBanks = response.map((bank) => ({
          value: bank.id,
          label: bank.bankName,
        }));
        setBankList(formattedBanks);
      } else {
        toast.error("Failed to load banks data");
        console.error("Invalid banks data:", response);
      }
    } catch (error) {
      toast.error("Failed to fetch banks");
      console.error("Fetch error:", error);
      throw error;
    }
  };
  useEffect(() => {
    if (location.state?.dashboardData && districtList.length > 0) {
      const selectedId = String(
        location.state.dashboardData.selectedDistrict ||
          location.state.dashboardData.parentregionid
      );
      const district = districtList.find((d) => String(d.id) === selectedId);

      // Set both DistrictId and dashboardConfig
      setDistrictId(selectedId); // Add this line
      setDashboardConfig((prev) => ({
        ...prev,
        selectedDistrict: selectedId,
        selectedDistrictName: district ? district.value : "",
      }));
    }
  }, [districtList, location.state?.dashboardData]);

  console.log(matchingDistrictName, "matching");

  const fetchUserProfile = async () => {
    try {
      const response = await getData(getThemeURL);
      if (response && Array.isArray(response) && response.length > 0) {
        setRoleName(response);

        // Set user role for easier access
        const role = response[0]?.role_name?.toUpperCase();
        const roleForHO = response[0]?.is_admin;
        setUserRole(role);

        if (role === "BRANCH OFFICER") {
          setUserRegionName(response[0]?.region_name || "");

          setDashboardConfig((prev) => ({
            ...prev,
            selectedDistrict:
              response[0]?.region_id?.toString() ||
              response[0]?.id?.toString() ||
              "",
            selectedDistrictName: response[0]?.region_name || "",
          }));

          // Fetch users using branch_id instead of region_id
          if (response[0]?.branch_id) {
            await fetchUsersByBranchId(response[0].branch_id);
          }
        }

        // If Head Office Admin, fetch blocks after districts are loaded
        if (roleForHO === true && districtList.length > 0) {
          await fetchBlocksForDistrict(districtList[0].id);
        }
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
    }
  };

  useEffect(() => {
    if (location.state?.dashboardData) {
      const initialData = initializeDashboard();
      setDashboardConfig({
        title: initialData.name || "",
        description: initialData.description,
        selectedDistrict: initialData.selectedDistrict,
        selectedDistricts: initialData.selectedDistricts || [],
        selectedBankIds: initialData.selectedBankIds || [],
        selectedUserIds: initialData.selectedUserIds,
        globalFilters: initialData.filters, // This will now have the correct values
        permissions: initialData.permissions,
        defaultBankId: initialData.defaultBankId,
      });
    }
  }, [location.state?.dashboardData, initializeDashboard]);

  console.log(dashboardConfig, "confconf");

  console.log(roleName, ".........................");

  const fetchBlocksForDistrict = async (districtId) => {
    // setLoadingBlocks(true);
    try {
      const response = await postDataWithBody(
        getblocksfromdistid,
        [districtId], // Send as array as per your existing pattern
        {}
      );

      if (response && Array.isArray(response)) {
        // Transform the response to match the expected format
        const formattedBlocks = response.map((block) => ({
          id: block.id,
          value: block.name || block.value,
          label: block.name || block.value,
        }));
        setBlockList(formattedBlocks);
      } else {
        console.error("Invalid blocks data format:", response);
        toast.error("Failed to load blocks data");
      }
    } catch (error) {
      console.error("Error fetching blocks:", error);
      toast.error("Failed to load blocks data");
    } finally {
      // setLoadingBlocks(false);
    }
  };
  const fetchUsersByBranchId = async (branchId) => {
    try {
      const response = await getData(
        getUserlistByBank.replace("BANKID", branchId)
      );
      if (Array.isArray(response)) {
        setUserlist(response);
      }
      console.log(userlist, "userlistuserlistuserlistuserlist");
    } catch (error) {
      setUserlist([]);
      // Handle error
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      await fetchBanks();
      await fetchUserProfile();

      // Updated: Use branch_id from roleName (theme URL response) instead of region_id
      if (roleName?.[0]?.is_admin === true && roleName?.[0]?.bank_id) {
        await fetchUsersByBranchId(roleName[0].bank_id);
      }
    };

    initializeData();
  }, [userRegionName]); // Added roleName to dependencies

  useEffect(() => {
    // Only update if we have userlist and we're editing an existing dashboard
    if (userlist.length > 0 && location.state?.dashboardData) {
      const initialData = initializeDashboard();

      // Ensure selectedUserIds are valid (exist in userlist)
      const validUserIds = initialData.selectedUserIds.filter((userId) =>
        userlist.some((user) => String(user.id) === String(userId))
      );

      if (
        validUserIds.length !== dashboardConfig.selectedUserIds.length ||
        !validUserIds.every((id) =>
          dashboardConfig.selectedUserIds.includes(id)
        )
      ) {
        setDashboardConfig((prev) => ({
          ...prev,
          selectedUserIds: validUserIds,
        }));
      }
    }
  }, [userlist, location.state?.dashboardData]);

  // Add another useEffect to handle block loading when role and districts are available
  useEffect(() => {
    if (
      roleName?.[0]?.is_admin === true &&
      districtList.length > 0 &&
      blockList.length === 0
    ) {
      fetchBlocksForDistrict(districtList[0].id);
    }
  }, [roleName, districtList]);

  const saveDashboardDetails = (name, description) => {
    setDashboardName(name);
    setDashboardDescription(description);
    // saveDashboardToLocalStorage();
  };
  useEffect(() => {
    if (location.state?.fromCreatePage) {
      setCurrentStep(1);
      setShowPopup(true);
      setPopupContext(location.state?.popupContext || "create");
    }
  }, [location.state?.fromCreatePage]);
  const handleAddWidget = () => {
    setEditMode("widget"); // Set mode to widget creation
    setPopupContext("addWidget");
    setCurrentStep(3);
    setShowPopup(true);
    resetNewItem();
  };
  const handleEditDashboard = () => {
    setEditMode("dashboard"); // Set mode to dashboard editing only
    setCurrentStep(1); // Start from dashboard configuration
    setShowPopup(true);
    // Don't reset newItem for dashboard editing
  };

  const [xAxisOptions, setXAxisOptions] = useState([]);
  const [yAxisOptions, setYAxisOptions] = useState([]);

  const dropdownStyle = {
    width: "100%",
    padding: "10px 12px",
    borderRadius: "8px",
    border: "1px solid #d1d5db",
    backgroundColor: "white",
    fontSize: "14px",
    color: "#374151",
    outline: "none",
  };

  const labelStyle = {
    display: "block",
    marginBottom: "8px",
    fontSize: "14px",
    fontWeight: "bold",
    color: "#374151",
  };

  const chartTypes = [
    "line",
    "area",
    "bar",
    "pie",
    "composed",
    "stackedBar",
    "radar",
    "trend",
    "progressBar",
    "radialProgress",
  ];

  const totalSteps = 3;
  const [currentStep, setCurrentStep] = useState(1);
  const nextStep = () => {
    // Handle step-specific saving logic before moving to next step
    if (currentStep === 1) {
      // Save dashboard configuration for step 1
      if (!validateDashboardStepOne()) return;
      setDashboardName(dashboardConfig.title);
      setDashboardDescription(dashboardConfig.description);
      saveDashboardDetails(dashboardConfig.title, dashboardConfig.description);
    }

    // Move to next step if not at the last step
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };
  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };
  const handleClose = () => {
    setShowPopup(false);
    setCurrentStep(1);
    // Only navigate if this popup was opened from "Create Dashboard"
    if (popupContext === "create") {
      setDashboardName("New Dashboard");
      navigate("/dynamic-dashboard-api/dashboardbuilderForApi");
    }

    // Reset the context
    setPopupContext(null);
  };
  const [selectedBankId, setSelectedBankId] = useState(null);
  const [selectedBranchId, setSelectedBranchId] = useState(null);

  const [selectionData, setSelectionData] = useState({
    bankData: null,
    branchData: null,
  });

  // Add these new states alongside your existing ones
  const [selectedBankIds, setSelectedBankIds] = useState([]); // For multi-bank selection
  const [selectedBranchIds, setSelectedBranchIds] = useState([]); // For multi-branch selection
  const [selectAllBanks, setSelectAllBanks] = useState({}); // Track "select all" state per bank
  const [instanceBankIds, setInstanceBankIds] = useState([]);

  const handleMultiBankSelect = (bankIds, bankData = []) => {
    console.log("Multi-bank selection:", { bankIds, bankData });
    setSelectedBankIds(bankIds);
    // Don't set instanceBankIds here - let handleMultiBranchSelect handle it

    // Clear single selections when using multi-selection
    setSelectedBankId(null);
    setSelectedBranchId(null);
  };

  const handleMultiBranchSelect = (bankIds, branchIds, selectionData = {}) => {
    console.log("Multi-branch selection received:", {
      bankIds,
      branchIds,
      selectionData,
    });

    // setSelectedBankIds(bankIds);
    setSelectedBranchIds(branchIds);

    // The TreeListComponent now calculates the combined instance bank IDs
    // which includes both directly selected banks AND banks with selected branches
    if (
      selectionData.instanceBankIds &&
      Array.isArray(selectionData.instanceBankIds)
    ) {
      console.log("Setting instance bank IDs:", selectionData.instanceBankIds);
      setInstanceBankIds(selectionData.instanceBankIds);
    } else {
      console.log(
        "No instanceBankIds in selectionData, using bankIds:",
        bankIds
      );
      setInstanceBankIds(bankIds);
    }

    // Clear single selections
    setSelectedBankId(null);
    setSelectedBranchId(null);
  };

  // Enhanced bank selection handler
  const handleBankSelect = (bankId, bankData = null) => {
    console.log("Bank selection:", { bankId, bankData });

    // If clicking the same bank, deselect it
    if (selectedBankId === bankId) {
      setSelectedBankId(null);
      setSelectedBranchId(null); // Clear branch when deselecting bank
      setSelectionData({ bankData: null, branchData: null });
      console.log("Deselected bank:", bankId);
      return;
    }

    // Select new bank and clear any branch selection
    setSelectedBankId(bankId);
    setSelectedBranchId(null);
    setSelectionData({
      bankData: bankData,
      branchData: null,
    });
  };

  // Enhanced branch selection handler
  const handleBranchSelect = (
    branchId,
    branchData = null,
    parentBankId = null
  ) => {
    console.log("Branch selection:", { branchId, branchData, parentBankId });

    // If clicking the same branch, deselect it
    if (selectedBranchId === branchId) {
      setSelectedBranchId(null);
      setSelectionData((prev) => ({ ...prev, branchData: null }));
      console.log("Deselected branch:", branchId);
      return;
    }

    // Select new branch and ensure parent bank is selected
    setSelectedBranchId(branchId);
    if (parentBankId && parentBankId !== selectedBankId) {
      setSelectedBankId(parentBankId);
    }

    setSelectionData((prev) => ({
      ...prev,
      branchData: branchData,
    }));
  };

  // Clear all selections
  const handleClearSelections = () => {
    setSelectedBankId(null);
    setSelectedBranchId(null);
    setSelectionData({ bankData: null, branchData: null });
    console.log("Cleared all selections");
  };

  // Get current selection info
  const getCurrentSelection = () => {
    return {
      bankId: selectedBankId,
      branchId: selectedBranchId,
      selectionType: selectedBranchId
        ? "branch"
        : selectedBankId
        ? "bank"
        : "none",
      data: selectionData,
    };
  };

  React.useEffect(() => {
    const fetchHOBankListData = async () => {
      // Only execute if the condition is met
      if (newItem.type !== "HO_bank_list" || newItem.dataSource) {
        return; // Exit early if not HO_bank_list or data already fetched
      }

      const serviceName = "bank_branch_details";
      setLoader(true);

      // Find matching query for bank_branch_details
      const matchedQuery = savedQueries.find(
        (q) => q.serviceName === serviceName
      );

      if (!matchedQuery) {
        console.log("No query found for bank_branch_details");
        setLoader(false);
        return;
      }

      const queryObject =
        typeof matchedQuery.queryBuilder === "string"
          ? JSON.parse(matchedQuery.queryBuilder)
          : matchedQuery.queryBuilder;

      // **NEW**: Helper function to determine filter fields dynamically
      const determineFilterFields = (queryObject) => {
        // Extract table name from entity
        const entity = queryObject.entity || "";
        const tableName = entity.includes(".") ? entity.split(".")[1] : entity;

        // Get joins information
        const joins = queryObject.joins || [];
        const joinedTables = joins.map((join) => {
          const table = join.table || join.alias || "";
          return table.includes(".") ? table.split(".")[1] : table;
        });

        // Helper function to determine filter field names
        let bankFilterField = null;

        // Check what tables are available
        const hasBankTable =
          tableName === "bank" || joinedTables.includes("bank");
        const hasJoins = joins.length > 0;

        console.log("HO Bank List: Table analysis:", {
          tableName,
          joinedTables,
          hasBankTable,
          hasJoins,
        });

        // Determine bank filter field
        if (tableName === "bank") {
          // If entity is bank table
          bankFilterField = "bank.id";
        } else if (hasBankTable && hasJoins) {
          // If joins are there and bank table is available
          bankFilterField = "bank.id";
        } else if (
          !hasBankTable &&
          tableName !== "bank" &&
          tableName !== "branch_details"
        ) {
          // For core tables (not bank or branch_details), use table.bank_id
          bankFilterField = `${tableName}.bank_id`;
        } else if (!hasBankTable) {
          // If no bank table available, use current table with bank_id
          bankFilterField = `${tableName}.bank_id`;
        }

        console.log(
          "HO Bank List: Determined bank filter field:",
          bankFilterField
        );

        return { bankFilterField };
      };

      const filters = {};
      console.log("serviceName", serviceName);
      console.log("queryObject", queryObject);

      try {
        const userResponse = await getData(getThemeURL);
        if (
          userResponse &&
          Array.isArray(userResponse) &&
          userResponse.length > 0
        ) {
          const user = userResponse[0];

          console.log("User role processing for HO Bank List");

          // **NEW**: Apply dynamic filter logic
          if (user.role_name === "SUPER ADMIN") {
            // For Super Admin, use selectedBankIds if available
            if (
              dashboardConfig.selectedBankIds &&
              dashboardConfig.selectedBankIds.length > 0
            ) {
              // **NEW**: Use dynamic filter field determination
              const { bankFilterField } = determineFilterFields(queryObject);

              if (bankFilterField) {
                filters[bankFilterField] = {
                  operator: "IN",
                  value: dashboardConfig.selectedBankIds.map(Number),
                };
                console.log(
                  "Applied dynamic bank filter for Super Admin in HO Bank List:",
                  filters[bankFilterField]
                );
              }
            }
          } else {
            // For non-Super Admin users, use their specific IDs with dynamic field determination
            const { bankFilterField } = determineFilterFields(queryObject);

            if (user.bank_id && bankFilterField) {
              filters[bankFilterField] = {
                operator: "IN",
                value: [user.bank_id], // Convert single ID to array format
              };
              console.log(
                "Applied dynamic bank filter for regular user in HO Bank List:",
                filters[bankFilterField]
              );
            }

            // **ADDITIONAL**: Also apply branch filter for non-super admin users if needed
            // Check if branch filtering is required based on query structure
            if (user.branch_id) {
              // Determine if we need branch filtering based on table structure
              const entity = queryObject.entity || "";
              const tableName = entity.includes(".")
                ? entity.split(".")[1]
                : entity;
              const joins = queryObject.joins || [];
              const joinedTables = joins.map((join) => {
                const table = join.table || join.alias || "";
                return table.includes(".") ? table.split(".")[1] : table;
              });

              // Apply branch filter if branch_details table is involved
              if (
                tableName === "branch_details" ||
                joinedTables.includes("branch_details")
              ) {
                filters["branch_details.branch_id"] = {
                  operator: "IN",
                  value: [user.branch_id],
                };
                console.log(
                  "Applied branch filter for regular user:",
                  filters["branch_details.branch_id"]
                );
              } else if (
                tableName.includes("branch") ||
                joinedTables.some((table) => table.includes("branch"))
              ) {
                filters[`${tableName}.branch_id`] = {
                  operator: "IN",
                  value: [user.branch_id],
                };
                console.log(
                  "Applied dynamic branch filter for regular user:",
                  filters[`${tableName}.branch_id`]
                );
              }
            }
          }
        }
      } catch (error) {
        console.error("Error fetching user profile for filters:", error);
      }

      const enhancedQuery = {
        ...queryObject,
        filters: {
          ...queryObject.filters,
          ...filters,
        },
      };
      console.log(
        "HO Bank List: Query sent to /nifi/fetchQuery:",
        enhancedQuery
      );
      console.log("HO Bank List: Applied filters:", filters);
      // Same auth logic as before
      let userResponse;
      try {
        userResponse = await getData(getThemeURL);
      } catch (error) {
        console.error("Card: Error fetching user profile:", error);
      }

      if (
        !userResponse ||
        !Array.isArray(userResponse) ||
        userResponse.length === 0
      ) {
        console.error("Card: Failed to fetch user profile or invalid response");
      }

      const user = userResponse[0];
      const roleName = user.role_name;
      const userBankId = user.bank_id;

      const authObject = {
        instanceUrl: "/query-builder-api/execute",
        apiType: "POST",
      };

      const processBankIds = (ids) => {
        if (!ids) return [];

        if (Array.isArray(ids)) {
          return ids.map((id) => Number(id));
        } else if (typeof ids === "string") {
          return ids.split(",").map((id) => Number(id.trim()));
        } else {
          return [Number(ids)];
        }
      };

      console.log(
        "dashboardConfig.selectedBankIds",
        dashboardConfig.selectedBankIds
      );
      // Set auth based on user role
      if (roleName === "SUPER ADMIN") {
        authObject.type = "SA";
        // Ensure we have valid selectedBankIds before processing
        if (
          dashboardConfig.selectedBankIds &&
          dashboardConfig.selectedBankIds.length > 0
        ) {
          authObject.bank_id = processBankIds(dashboardConfig.selectedBankIds);
        } else {
          // Handle case when no banks are selected - this might be the issue
          console.warn(
            "No selectedBankIds found for SA user, using default behavior"
          );
          authObject.bank_id = null; // or some appropriate default
        }
      } else if (user.is_admin === true) {
        authObject.type = "HO";
        authObject.bank_id = processBankIds(userBankId);
      } else {
        authObject.type = "U";
        authObject.bank_id = processBankIds(userBankId);
      }

      const payloadWithAuth = {
        auth: authObject,
        main: enhancedQuery,
      };
      try {
        const response = await fetch("/nifi/fetchQuery-API", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payloadWithAuth),
        });

        const result = await response.json();
        console.log("HO Bank List Data - Auto Fetch Result:", result);

        // Extract aggregatedData from the response
        const aggregatedData = result.aggregatedData;

        if (
          aggregatedData &&
          aggregatedData.data &&
          aggregatedData.data.length > 0
        ) {
          // Get all available fields from the first data item in aggregatedData
          const availableFields = Object.keys(aggregatedData.data[0] || {});

          // Separate bank and branch fields (excluding ID fields from UI)
          const bankFields = availableFields.filter(
            (field) => field.startsWith("bank_") && field !== "bank_id"
          );
          const branchFields = availableFields.filter(
            (field) =>
              field.startsWith("branch") && field !== "branchdetails_id"
          );

          // Initialize field configurations for bank fields
          const bankFieldConfigs = bankFields.map((field) => ({
            fieldName: field,
            color: "#000000",
            fontSize: "small",
            fontSizeValue: 12,
            textAlignment: "left",
            enabled: true,
            category: "bank",
          }));

          // Initialize field configurations for branch fields
          const branchFieldConfigs = branchFields.map((field) => ({
            fieldName: field,
            color: "#000000",
            fontSize: "small",
            fontSizeValue: 12,
            textAlignment: "left",
            enabled: true,
            category: "branch",
          }));

          // Combine all field configs
          const allFieldConfigs = [...bankFieldConfigs, ...branchFieldConfigs];

          // Create fieldsConfig for field visibility
          const fieldsConfig = allFieldConfigs.map((field) => ({
            fieldName: field.fieldName,
            enabled: true, // All fields enabled by default
          }));

          // Create cardStyles object instead of embedding in data
          const cardStyles = {};
          allFieldConfigs.forEach((config) => {
            if (config.enabled) {
              cardStyles[`${config.fieldName}_color`] = config.color;
              cardStyles[`${config.fieldName}_fontSize`] = config.fontSizeValue;
              cardStyles[`${config.fieldName}_textAlignment`] =
                config.textAlignment;
            }
          });

          // Additional card styles for HO_bank_list
          cardStyles.card_color = newItem.color || "#ffffff";
          cardStyles.bank_card_color = "#ffffff";
          cardStyles.branch_card_color = "#ffffff";
          cardStyles.expand_color = "#000000";

          // Get enabled fields for data filtering
          const enabledFields = fieldsConfig
            .filter((config) => config.enabled)
            .map((config) => config.fieldName);

          // Include ID fields in data (needed for functionality)
          const fieldsToIncludeInData = [...enabledFields];
          if (availableFields.includes("bank_id")) {
            fieldsToIncludeInData.push("bank_id");
          }
          if (availableFields.includes("branch_id")) {
            fieldsToIncludeInData.push("branch_id");
          }
          if (availableFields.includes("branchdetails_id")) {
            fieldsToIncludeInData.push("branchdetails_id");
          }

          // Filter data to include enabled fields + ID fields from aggregatedData
          const filteredData = aggregatedData.data.map((item) => {
            const filteredItem = {};
            fieldsToIncludeInData.forEach((field) => {
              if (item.hasOwnProperty(field)) {
                filteredItem[field] = item[field];
              }
            });
            return filteredItem;
          });

          // Create data source configuration using aggregatedData
          const dataSourceConfig = {
            data: filteredData, // Pure data without config object
            columns: aggregatedData.columns || availableFields,
            serviceName: serviceName,
            availableFields: availableFields,
            originalData: aggregatedData.data,
            totalBanks: result.totalBanks,
            successfulBanks: result.successfulBanks,
            recordCount: aggregatedData.recordCount,
          };

          // Update newItem with all the processed data
          setNewItem({
            ...newItem,
            dataSource: serviceName,
            data: filteredData, // Pure data array from aggregatedData
            cardStyles: cardStyles, // Separate styles object
            fieldsConfig: fieldsConfig, // Separate field visibility config
            dataSourceConfig: dataSourceConfig,
            fieldConfigs: allFieldConfigs,
            allAvailableFields: availableFields,
            bankCardColor: "#ffffff",
            branchCardColor: "#ffffff",
          });
        } else {
          console.log("HO Bank List - No data returned in aggregatedData");
          setNewItem({
            ...newItem,
            dataSource: serviceName,
            data: [],
            cardStyles: {},
            fieldsConfig: [],
            dataSourceConfig: null,
            fieldConfigs: [],
            allAvailableFields: [],
            bankCardColor: "#ffffff",
            branchCardColor: "#ffffff",
          });
        }
      } catch (error) {
        console.error("Error fetching HO bank list data:", error);
        setNewItem({
          ...newItem,
          dataSource: "bank_branch_details",
          data: [],
          cardStyles: {},
          fieldsConfig: [],
          dataSourceConfig: null,
          fieldConfigs: [],
          allAvailableFields: [],
          bankCardColor: "#ffffff",
          branchCardColor: "#ffffff",
        });
      } finally {
        setLoader(false);
      }
    };

    fetchHOBankListData();
  }, [
    newItem.type,
    newItem.dataSource,
    savedQueries,
    dashboardConfig.selectedBankIds,
  ]);

  const handleAddItem = () => {
    if (newItem.isEditing && newItem.editId) {
      const queryBuilder = buildQueryForDataSource();

      if (newItem.type === "map") {
        // For maps with multiple data sources, save queries for each data source
        if (
          Array.isArray(newItem.dataSource) &&
          newItem.dataSource.length > 0
        ) {
          newItem.dataSource.forEach(async (dataSource) => {
            const mapQueryBuilder = buildQueryForMapDataSource(dataSource);
            if (mapQueryBuilder) {
              await saveWidgetQuery(mapQueryBuilder, dataSource);
            }
          });
        }
      }

      // Call save query API when updating widget
      if (queryBuilder) {
        saveWidgetQuery(queryBuilder);
      }
      // EDITING MODE - Update existing item
      setDashboardItems((prevItems) =>
        prevItems.map((item) => {
          if (item.id === newItem.editId) {
            if (item.type === "map") {
              console.log("newItem.dataSourcemap", newItem.dataSource);
              const points = [];
              newItem.dataSource?.forEach((source) => {
                const sourceData = dataSourcesForMap[source]?.sampleData || [];
                const fieldsConfig = newItem.fieldsBySource[source] || [];
                sourceData.forEach((dataItem) => {
                  const pointData = {
                    lat: dataItem.lat || dataItem.latitude || 25.5941,
                    lng: dataItem.lng || dataItem.longitude || 85.1376,
                    type: source,
                  };

                  fieldsConfig
                    .filter((field) => field.selected)
                    .sort((a, b) => (a.order || 0) - (b.order || 0))
                    .forEach((field) => {
                      pointData[field.key] = dataItem[field.key];
                    });

                  points.push(pointData);
                });
              });

              console.log("Updated Map Points:", points);

              return {
                ...item,
                title: newItem.title,
                title_color:
                  newItem.title_color || item.title_color || "#000000",
                title_fontSize:
                  newItem.title_fontSize || item.title_fontSize || 16,
                title_textAlignment:
                  newItem.title_textAlignment ||
                  item.title_textAlignment ||
                  "left",
                mapType: newItem.mapType || item.mapType || "standard",
                data: points.length > 0 ? { points } : newItem.data,
                center: newItem.center || item.center,
                zoom: newItem.zoom || item.zoom || 13,
                showMarkers: newItem.showMarkers !== false,
                showHeatmap: newItem.showHeatmap || false,
                clusterMarkers: newItem.clusterMarkers || false,
                dataSource: newItem.dataSource || item.dataSource,
                fieldsBySource: newItem.fieldsBySource || item.fieldsBySource,
                fromDate: newItem.fromDate || "",
                toDate: newItem.toDate || "",
                selectedDateField: newItem.selectedDateField || "",
              };
            } else if (item.type === "chart") {
              console.log("NEWITEM Chart", newItem);

              // Enhanced date filtering for chart editing
              let dateFilterKey = null;
              const originalData = newItem.data || [];
              if (originalData.length > 0) {
                const availableKeys = Object.keys(originalData[0]);

                // Use created_on if available, regardless of selected xAxisKey
                if (availableKeys.includes("created_on")) {
                  dateFilterKey = "created_on";
                } else if (
                  newItem.xAxisKey === "month" ||
                  newItem.xAxisKey.toLowerCase().includes("date")
                ) {
                  dateFilterKey = newItem.xAxisKey;
                }
              }

              let filteredData = [...originalData];
              if ((dateRange.from || dateRange.to) && dateFilterKey) {
                filteredData = filterDataByDateRange(
                  originalData,
                  dateFilterKey,
                  dateRange.from,
                  dateRange.to
                );
              }

              const validYAxisKeys = newItem.yAxisKeys.filter(
                (key) =>
                  originalData.length === 0 ||
                  (originalData[0] &&
                    Object.keys(originalData[0]).includes(key))
              );

              const finalYAxisKeys =
                validYAxisKeys.length > 0
                  ? validYAxisKeys
                  : originalData.length > 0
                  ? [Object.keys(originalData[0])[1] || ""]
                  : [""];

              const updatedColors =
                newItem.colors && newItem.colors.length >= finalYAxisKeys.length
                  ? newItem.colors.slice(0, finalYAxisKeys.length)
                  : [
                      ...(newItem.colors || []),
                      ...Array(
                        Math.max(
                          0,
                          finalYAxisKeys.length - (newItem.colors?.length || 0)
                        )
                      )
                        .fill()
                        .map(
                          (_, i) =>
                            chartColors[
                              ((newItem.colors?.length || 0) + i) %
                                chartColors.length
                            ]
                        ),
                    ];

              return {
                ...item,
                title: newItem.title,
                title_color:
                  newItem.title_color || item.title_color || "#000000",
                title_fontSize:
                  newItem.title_fontSize || item.title_fontSize || 16,
                title_textAlignment:
                  newItem.title_textAlignment ||
                  item.title_textAlignment ||
                  "left",
                chartType: newItem.chartType,
                dataSource: newItem.dataSource || item.dataSource,
                data: filteredData,
                originalData: originalData,
                selectedDateField: newItem.selectedDateField || "",
                appliedDateRange: {
                  from: dateRange.from,
                  to: dateRange.to,
                },
                xAxisKey:
                  newItem.xAxisKey ||
                  (originalData.length > 0
                    ? Object.keys(originalData[0])[0]
                    : item.xAxisKey),
                yAxisKeys: finalYAxisKeys,
                yAxisLabels: newItem.yAxisLabels || [],
                pieColors: newItem.pieColors || [],
                xAxisLabelFontSize:
                  newItem.xAxisLabelFontSize || item.xAxisLabelFontSize || 14,
                yAxisLabelFontSize:
                  newItem.yAxisLabelFontSize || item.yAxisLabelFontSize || 14,
                legendFontSize:
                  newItem.legendFontSize || item.legendFontSize || 14,
                showDownloadExcelChart:
                  newItem.showDownloadExcelChart ?? item.showDownloadExcelChart,
                showDownloadPDFChart:
                  newItem.showDownloadPDFChart ?? item.showDownloadPDFChart,
                showBankLogo: newItem.showBankLogo ?? item.showBankLogo,
                showChartTitle: newItem.showChartTitle ?? item.showChartTitle,
                pdfTitle:
                  newItem.pdfTitle !== undefined
                    ? newItem.pdfTitle
                    : item.pdfTitle,
                enablePDFTitle: newItem.enablePDFTitle ?? item.enablePDFTitle,
                pdfChartTitle:
                  newItem.pdfChartTitle !== undefined
                    ? newItem.pdfChartTitle
                    : item.pdfChartTitle,
                enableChartPDFTitle:
                  newItem.enableChartPDFTitle ?? item.enableChartPDFTitle,
                colors: updatedColors,
                fromDate: newItem.fromDate || "",
                toDate: newItem.toDate || "",
                gridLines:
                  newItem.gridLines !== undefined
                    ? newItem.gridLines
                    : item.gridLines,
                curveType: newItem.curveType || item.curveType,
                legendPosition: newItem.legendPosition || item.legendPosition,
                animation: newItem.animation || item.animation,
                xAxisOrientation:
                  newItem.xAxisOrientation ||
                  item.xAxisOrientation ||
                  "horizontal",
                ...(newItem.chartType === "bar" && {
                  layout: newItem.layout || item.layout,
                  barWidth: newItem.barWidth || item.barWidth,
                }),
                ...(newItem.chartType === "pie" && {
                  innerRadius:
                    newItem.innerRadius !== undefined
                      ? newItem.innerRadius
                      : item.innerRadius,
                  showLabels:
                    newItem.showLabels !== undefined
                      ? newItem.showLabels
                      : item.showLabels,
                  legendPosition: newItem.legendPosition || item.legendPosition,
                }),
                ...(newItem.chartType === "area" && {
                  areaOpacity: newItem.areaOpacity || item.areaOpacity,
                  showGradient:
                    newItem.showGradient !== undefined
                      ? newItem.showGradient
                      : item.showGradient,
                }),
              };
            } else if (item.type === "table") {
              const originalData = newItem.dataSource
                ? dataSourcesForTable[newItem.dataSource]?.data ||
                  item.data ||
                  []
                : item.data || [];

              let sourceData = [...originalData];
              if (
                newItem.dateFilter &&
                (newItem.dateFilter.startDate || newItem.dateFilter.endDate)
              ) {
                sourceData = originalData.filter((row) => {
                  if (!row.created_on) return true;
                  const rowDate = new Date(row.created_on);
                  const startValid =
                    !newItem.dateFilter.startDate ||
                    rowDate >= new Date(newItem.dateFilter.startDate);
                  const endValid =
                    !newItem.dateFilter.endDate ||
                    rowDate <=
                      new Date(
                        new Date(newItem.dateFilter.endDate).setHours(
                          23,
                          59,
                          59,
                          999
                        )
                      );
                  return startValid && endValid;
                });
              }

              const updatedColumns = newItem.columns.map((col) => ({
                ...col,
                selected: col.selected !== undefined ? col.selected : true,
              }));

              const updatedSubColumns =
                newItem.subColumns?.map((col) => ({
                  ...col,
                  selected: col.selected !== undefined ? col.selected : true,
                })) || [];

              return {
                ...item,
                title: newItem.title,
                title_color:
                  newItem.title_color || item.title_color || "#000000",
                title_fontSize:
                  newItem.title_fontSize || item.title_fontSize || 16,
                title_textAlignment:
                  newItem.title_textAlignment ||
                  item.title_textAlignment ||
                  "left",
                dataSource: newItem.dataSource || item.dataSource,
                columns: updatedColumns,
                subColumns: updatedSubColumns,
                data: sourceData,
                originalData: originalData,
                dateFilter: newItem.dateFilter || null,
                showDownload: newItem.showDownload ?? item.showDownload,
                showPDFDownload:
                  newItem.showPDFDownload ?? item.showPDFDownload,
                pdfTitle:
                  newItem.pdfTitle !== undefined
                    ? newItem.pdfTitle
                    : item.pdfTitle,
                enablePDFTitle: newItem.enablePDFTitle ?? item.enablePDFTitle,
                showBankLogo:
                  newItem.showBankLogo ?? item.showBankLogo ?? false,
                showDateFilter: newItem.showDateFilter ?? item.showDateFilter,
                showSearch: newItem.showSearch ?? item.showSearch,
                showPagination: newItem.showPagination ?? item.showPagination,
                enableSorting: newItem.enableSorting ?? item.enableSorting,
                tableType: newItem.tableType || item.tableType,
                headerColor:
                  newItem.headerColor || item.headerColor || "#f9fafb",
                headerTextColor:
                  newItem.headerTextColor || item.headerTextColor || "#374151",
                subHeaderColor: newItem.subHeaderColor || "#f1f5f9",
                subHeaderTextColor: newItem.subHeaderTextColor || "#334155",
                fromDate: newItem.fromDate || item.fromDate,
                toDate: newItem.toDate || item.toDate,
                selectedDateField: newItem.selectedDateField || "",
              };
            } else if (item.type === "card") {
              console.log("NEWITEM", newItem);
              let sourceData = newItem.data || [];
              let calculatedValue = newItem.value;

              if (
                newItem.dataSource &&
                sourceData.length > 0 &&
                newItem.valueKey
              ) {
                calculatedValue = sourceData[0][newItem.valueKey];
              }

              if (
                (dateRange.from || dateRange.to) &&
                newItem.valueKey === "date"
              ) {
                sourceData = filterDataByDateRange(
                  sourceData,
                  "date",
                  dateRange.from,
                  dateRange.to
                );
                if (sourceData.length > 0 && newItem.valueKey) {
                  calculatedValue = sourceData[0][newItem.valueKey];
                }
              }

              return {
                ...item,
                title: newItem.title,
                title_color:
                  newItem.title_color || item.title_color || "#000000",
                title_fontSize:
                  newItem.title_fontSize || item.title_fontSize || 16,
                title_textAlignment:
                  newItem.title_textAlignment ||
                  item.title_textAlignment ||
                  "left",
                value: calculatedValue,
                valueKey: newItem.valueKey || item.valueKey,
                dataSource: newItem.dataSource || item.dataSource,
                dataSourceConfig:
                  newItem.dataSourceConfig || item.dataSourceConfig,
                data: sourceData,
                cardStyles: newItem.cardStyles || item.cardStyles || {},
                fieldsConfig: newItem.fieldsConfig || item.fieldsConfig || [], // Add fieldsConfig
                color: newItem.color || item.color || "#ffffff",
                icon: newItem.icon || item.icon || "FiDollarSign",
                widgetSize: newItem.widgetSize || item.widgetSize || "small",
                textColor: newItem.textColor || item.textColor || "#1f2937",
                fontSize: newItem.fontSize || item.fontSize || "medium",
                customFontSize: newItem.customFontSize || item.customFontSize,
                textAlignment:
                  newItem.textAlignment || item.textAlignment || "left",
                trend: newItem.trend || item.trend,
                trendValue: newItem.trendValue || item.trendValue,
                trendColor: newItem.trendColor || item.trendColor,
                fromDate: newItem.fromDate || item.fromDate,
                toDate: newItem.toDate || item.toDate,
                subtitle: newItem.subtitle || item.subtitle,
                selectedDateField: newItem.selectedDateField || "",
              };
            } else if (item.type === "HO_bank_list") {
              console.log("NEWITEM HO_bank_list", newItem);
              let sourceData = [];
              let calculatedValue = newItem.value;

              // Handle data source configuration
              if (
                newItem.dataSource &&
                newItem.data &&
                newItem.data.length > 0
              ) {
                // Use the configured data
                sourceData = newItem.data;
              } else if (newItem.dataSource && newItem.dataSourceConfig) {
                sourceData = newItem.dataSourceConfig.data || [];
              } else {
                sourceData = newItem.data || [];
              }

              // Apply date filtering if needed
              if (
                (dateRange.from || dateRange.to) &&
                newItem.valueKey === "date"
              ) {
                const filteredSourceData = filterDataByDateRange(
                  sourceData,
                  "date",
                  dateRange.from,
                  dateRange.to
                );

                sourceData = filteredSourceData;

                if (sourceData.length > 0 && newItem.valueKey) {
                  calculatedValue = sourceData[0][newItem.valueKey];
                }
              }

              return {
                ...item,
                title: newItem.title,
                title_color:
                  newItem.title_color || item.title_color || "#000000",
                title_fontSize:
                  newItem.title_fontSize || item.title_fontSize || 16,
                title_textAlignment:
                  newItem.title_textAlignment ||
                  item.title_textAlignment ||
                  "left",
                value: calculatedValue,
                valueKey: newItem.valueKey || item.valueKey,
                dataSource: newItem.dataSource || item.dataSource,
                dataSourceConfig:
                  newItem.dataSourceConfig || item.dataSourceConfig,
                data: sourceData,
                cardStyles: newItem.cardStyles || item.cardStyles || {}, // Separate styles object
                fieldsConfig: newItem.fieldsConfig || item.fieldsConfig || [], // Field visibility config
                color: newItem.color || item.color || "#ffffff",
                icon: newItem.icon || item.icon || "FiBank",
                widgetSize: newItem.widgetSize || item.widgetSize || "small",
                textColor: newItem.textColor || item.textColor || "#1f2937",
                fontSize: newItem.fontSize || item.fontSize || "medium",
                customFontSize: newItem.customFontSize || item.customFontSize,
                textAlignment:
                  newItem.textAlignment || item.textAlignment || "left",
                trend: newItem.trend || item.trend,
                trendValue: newItem.trendValue || item.trendValue,
                trendColor: newItem.trendColor || item.trendColor,
                fromDate: newItem.fromDate || item.fromDate,
                toDate: newItem.toDate || item.toDate,
                subtitle: newItem.subtitle || item.subtitle,
                // HO_bank_list specific properties
                bankList: newItem.bankList || item.bankList || [],
                showBankDetails:
                  newItem.showBankDetails ?? item.showBankDetails ?? true,
                bankListLayout:
                  newItem.bankListLayout || item.bankListLayout || "list",
                maxBanksToShow:
                  newItem.maxBanksToShow || item.maxBanksToShow || 10,
                // Field configuration properties
                fieldConfigs: newItem.fieldConfigs || item.fieldConfigs || [],
                allAvailableFields:
                  newItem.allAvailableFields || item.allAvailableFields || [],
                bankCardColor:
                  newItem.bankCardColor || item.bankCardColor || "#ffffff",
                branchCardColor:
                  newItem.branchCardColor || item.branchCardColor || "#ffffff",
                expand_color:
                  newItem.expand_color || item.expand_color || "#000000",
              };
            } else {
              // Default card type editing
              return {
                ...item,
                title: newItem.title,
                value: newItem.value,
                subtitle: newItem.subtitle,
                color: newItem.color,
                icon: newItem.icon,
                widgetSize: newItem.widgetSize,
                textColor: newItem.textColor,
                fontSize: newItem.fontSize,
                customFontSize: newItem.customFontSize,
                textAlignment: newItem.textAlignment,
              };
            }
          }
          return item;
        })
      );

      // Close edit popup and reset
      setShowEditPopup(false);
      setDateRange({ from: null, to: null });
    } else {
      // CREATION MODE - Add new item
      if (newItem.type === "chart") {
        const sourceData = [];
        // ? dataSourcesForChart[newItem.dataSource]?.data
        // : sampleChartData[newItem.chartType] || [];
        const validYAxisKeys = newItem.yAxisKeys.filter(
          (key) =>
            sourceData.length === 0 ||
            (sourceData[0] && Object.keys(sourceData[0]).includes(key))
        );

        const finalYAxisKeys =
          validYAxisKeys.length > 0
            ? validYAxisKeys
            : sourceData.length > 0
            ? [Object.keys(sourceData[0])[1]]
            : [""];

        const item = {
          ...newItem,
          id: generateId(),
          data: sourceData,
          xAxisKey:
            newItem.xAxisKey ||
            (sourceData.length > 0 ? Object.keys(sourceData[0])[0] : ""),
          yAxisKeys: finalYAxisKeys,
          colors:
            newItem.colors.length >= finalYAxisKeys.length
              ? newItem.colors.slice(0, finalYAxisKeys.length)
              : finalYAxisKeys.map(
                  (_, i) => chartColors[i % chartColors.length]
                ),
          gridLines: newItem.gridLines !== undefined ? newItem.gridLines : true,
          curveType: newItem.curveType || "monotone",
          legendPosition: newItem.legendPosition || "bottom",
          animation: newItem.animation || "default",
          ...(newItem.chartType === "bar" && {
            layout: newItem.layout || "horizontal",
            barWidth: newItem.barWidth || 30,
          }),
          ...(newItem.chartType === "pie" && {
            innerRadius:
              newItem.innerRadius !== undefined ? newItem.innerRadius : 0,
            showLabels:
              newItem.showLabels !== undefined ? newItem.showLabels : true,
          }),
          ...(newItem.chartType === "area" && {
            areaOpacity: newItem.areaOpacity || 50,
            showGradient:
              newItem.showGradient !== undefined ? newItem.showGradient : false,
          }),
        };
        setDashboardItems([...dashboardItems, item]);
      } else if (newItem.type === "table") {
        const sourceData = newItem.dataSource
          ? newItem.tableType === "basic"
            ? dataSourcesForTable[newItem.dataSource]?.data || []
            : dataSourcesForTable[newItem.dataSource]?.data || []
          : [];
        const filteredData = sourceData.map((row) => {
          const filteredRow = {};
          newItem.columns
            .filter((col) => col.selected)
            .forEach((col) => {
              filteredRow[col.key] = row[col.key];
            });
          return filteredRow;
        });

        const item = {
          ...newItem,
          id: generateId(),
          columns: newItem.columns.filter((col) => col.selected),
          subColumns: newItem.subColumns?.filter((col) => col.selected) || [],
          data: filteredData,
          showDownload:
            newItem.showDownload !== undefined ? newItem.showDownload : false,
          showDateFilter:
            newItem.showDateFilter !== undefined
              ? newItem.showDateFilter
              : false,
          showSearch:
            newItem.showSearch !== undefined ? newItem.showSearch : false,
          showPagination:
            newItem.showPagination !== undefined
              ? newItem.showPagination
              : false,
          enableSorting:
            newItem.enableSorting !== undefined ? newItem.enableSorting : false,
          tableType: newItem.tableType || "basic",
          headerColor: newItem.headerColor || "#f9fafb",
          headerTextColor: newItem.headerTextColor || "#374151",
          subHeaderColor: newItem.subHeaderColor || "#f1f5f9",
          subHeaderTextColor: newItem.subHeaderTextColor || "#334155",
        };
        setDashboardItems([...dashboardItems, item]);
      } else if (newItem.type === "map") {
        console.log("newItem.dataSourcemap new", newItem.dataSource);
        const points = [];
        newItem.dataSource?.forEach((source) => {
          const sourceData = dataSourcesForMap[source]?.sampleData || [];
          const fieldsConfig = newItem.fieldsBySource[source] || [];

          sourceData.forEach((dataItem) => {
            const pointData = {
              lat: dataItem.lat || dataItem.latitude || 25.5941,
              lng: dataItem.lng || dataItem.longitude || 85.1376,
              type: source,
            };

            fieldsConfig
              .filter((field) => field.selected)
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .forEach((field) => {
                pointData[field.key] = dataItem[field.key];
              });

            points.push(pointData);
          });
        });

        const item = {
          ...newItem,
          id: generateId(),
          data: { points },
          showMarkers: newItem.showMarkers !== false,
          showHeatmap: newItem.showHeatmap || false,
          clusterMarkers: newItem.clusterMarkers || false,
          zoom: newItem.zoom || 13,
        };
        setDashboardItems([...dashboardItems, item]);
      } else if (newItem.type === "HO_bank_list") {
        // Creation mode for HO_bank_list
        const item = {
          ...newItem,
          id: generateId(),
          color: newItem.color || "#ffffff",
          icon: newItem.icon || "FiBank",
          widgetSize: newItem.widgetSize || "small",
          textColor: newItem.textColor || "#1f2937",
          fontSize: newItem.fontSize || "medium",
          textAlignment: newItem.textAlignment || "left",
          // HO_bank_list specific properties
          bankList: newItem.bankList || [],
          showBankDetails:
            newItem.showBankDetails !== undefined
              ? newItem.showBankDetails
              : true,
          bankListLayout: newItem.bankListLayout || "list",
          maxBanksToShow: newItem.maxBanksToShow || 10,
        };
        setDashboardItems([...dashboardItems, item]);
      } else {
        // Default item creation (card, etc.)
        const item = {
          ...newItem,
          id: generateId(),
        };
        setDashboardItems([...dashboardItems, item]);
      }

      // Close create popup
      setShowPopup(false);
    }

    // Always reset the form
    resetNewItem();
  };

  const buildQueryForDataSource = () => {
    if (!newItem.dataSource) return null;

    try {
      // Find the matching saved query (PRIMARY query)
      const matchedQuery = savedQueries.find(
        (q) => q.serviceName === newItem.dataSource
      );

      if (!matchedQuery) {
        console.log("No matched query found for:", newItem.dataSource);
        return null;
      }

      console.log("Found PRIMARY query:", matchedQuery);

      // Get the base query (PRIMARY query structure)
      let primaryQuery;
      if (typeof matchedQuery.queryBuilder === "string") {
        try {
          primaryQuery = JSON.parse(matchedQuery.queryBuilder);
        } catch (parseError) {
          console.error(
            "Error parsing PRIMARY queryBuilder string:",
            parseError
          );
          return null;
        }
      } else if (typeof matchedQuery.queryBuilder === "object") {
        primaryQuery = matchedQuery.queryBuilder;
      } else {
        console.error("Invalid PRIMARY queryBuilder format");
        return null;
      }

      console.log("PRIMARY query structure:", primaryQuery);

      // For SECONDARY queries, we want to preserve the SAME structure as PRIMARY
      // Create a deep copy to avoid modifying the original
      let secondaryQuery = JSON.parse(JSON.stringify(primaryQuery));

      // Only add date filters if they exist in the widget
      if (newItem.fromDate && newItem.toDate) {
        const selectedDateField = newItem.selectedDateField;

        const selectedFieldData = newItemForDate.find(
          (field) => field.fullName === newItem.selectedDateField
        );
        const isTimestamp =
          selectedFieldData && selectedFieldData.type === "timestamp";
        if (isTimestamp) {
          // Append timestamp to fromDate and toDate for timestamp fields
          const fromDateTime = newItem.fromDate + " 00:00:00.000";
          const toDateTime = newItem.toDate + " 23:59:59.999"; // Use end of day for toDate

          secondaryQuery.filters = {
            ...secondaryQuery.filters,
            [`${selectedDateField}`]: {
              operator: "BETWEEN",
              value: [fromDateTime, toDateTime],
            },
          };
        } else {
          // Use date values as-is for date-only fields
          secondaryQuery.filters = {
            ...secondaryQuery.filters,
            [`${selectedDateField}`]: {
              operator: "BETWEEN",
              value: [newItem.fromDate, newItem.toDate],
            },
          };
        }
        // if (primaryQuery.entity) {
        const tableName =
          primaryQuery.entity.split(".")[1] || primaryQuery.entity;
        console.log(tableName, "tablenametablename");

        console.log(
          "Added date filters to SECONDARY query:",
          secondaryQuery.filters
        );
      }

      console.log(
        "SECONDARY query structure (preserving PRIMARY structure):",
        secondaryQuery
      );

      // Return the query with preserved PRIMARY structure
      const result = JSON.stringify(secondaryQuery);
      console.log("Final SECONDARY query string:", result);
      return result;
    } catch (error) {
      console.error("Error building query for data source:", error);
      return null;
    }
  };

  const buildQueryForMapDataSource = (dataSourceName) => {
    if (!dataSourceName) return null;

    try {
      // Find the matching saved query for this specific data source
      const matchedQuery = savedQueries.find(
        (q) => q.serviceName === dataSourceName
      );

      if (!matchedQuery) {
        console.log(
          "No matched query found for map data source:",
          dataSourceName
        );
        return null;
      }

      console.log("Found PRIMARY query for map data source:", matchedQuery);

      // Get the base query (PRIMARY query structure)
      let primaryQuery;
      if (typeof matchedQuery.queryBuilder === "string") {
        try {
          primaryQuery = JSON.parse(matchedQuery.queryBuilder);
        } catch (parseError) {
          console.error(
            "Error parsing PRIMARY queryBuilder string:",
            parseError
          );
          return null;
        }
      } else if (typeof matchedQuery.queryBuilder === "object") {
        primaryQuery = matchedQuery.queryBuilder;
      } else {
        console.error("Invalid PRIMARY queryBuilder format");
        return null;
      }

      console.log("PRIMARY query structure for map:", primaryQuery);

      // Create a deep copy to avoid modifying the original
      let secondaryQuery = JSON.parse(JSON.stringify(primaryQuery));

      // Add date filters if they exist in the map widget
      if (newItem.fromDate && newItem.toDate) {
        const selectedDateField = newItem.selectedDateField;
        const selectedFieldData = newItemForDate.find(
          (field) => field.fullName === newItem.selectedDateField
        );
        const isTimestamp =
          selectedFieldData && selectedFieldData.type === "timestamp";

        if (isTimestamp) {
          const fromDateTime = newItem.fromDate + " 00:00:00.000";
          const toDateTime = newItem.toDate + " 23:59:59.999";

          secondaryQuery.filters = {
            ...secondaryQuery.filters,
            [`${selectedDateField}`]: {
              operator: "BETWEEN",
              value: [fromDateTime, toDateTime],
            },
          };
        } else {
          secondaryQuery.filters = {
            ...secondaryQuery.filters,
            [`${selectedDateField}`]: {
              operator: "BETWEEN",
              value: [newItem.fromDate, newItem.toDate],
            },
          };
        }

        console.log(
          "Added date filters to map SECONDARY query:",
          secondaryQuery.filters
        );
      }

      console.log("Map SECONDARY query structure:", secondaryQuery);

      const result = JSON.stringify(secondaryQuery);
      console.log("Final map SECONDARY query string:", result);
      return result;
    } catch (error) {
      console.error("Error building query for map data source:", error);
      return null;
    }
  };

  const saveWidgetQuery = async (queryBuilder, specificDataSource = null) => {
    try {
      setLoader(true);

      console.log("saveWidgetQuery called with queryBuilder:", queryBuilder);

      // Get user profile for created_by and other fields
      const userResponse = await getData(getThemeURL);
      const user =
        userResponse && Array.isArray(userResponse) && userResponse.length > 0
          ? userResponse[0]
          : null;

      if (!user) {
        console.error("Unable to get user profile");
        return;
      }

      console.log("User profile retrieved:", user);

      const dataSourceToFind = specificDataSource || newItem.dataSource;

      // Find the original saved query to get existing data
      const originalQuery = savedQueries.find(
        (q) => q.serviceName === dataSourceToFind
      );

      if (!originalQuery) {
        console.error(
          "Original query not found for datasource:",
          newItem.dataSource
        );
        console.log(
          "Available saved queries:",
          savedQueries.map((q) => q.serviceName)
        );
        return;
      }

      console.log("Original PRIMARY query found:", originalQuery);

      // **ADD THIS: Get instance data to construct apiEndPointUrl**
      let apiEndPointUrl = null;
      try {
        const userProfile = await getData(getThemeURL);
        if (
          userProfile &&
          Array.isArray(userProfile) &&
          userProfile.length > 0
        ) {
          const user = userProfile[0];
          const roleName = user.role_name;

          let payload;

          if (roleName === "SUPER ADMIN") {
            payload = {
              type: "DEFAULT",
              bank_id: null,
            };
          } else if (user.is_admin === true) {
            payload = {
              type: "HO",
              bank_id: user.bank_id,
            };
          } else {
            payload = {
              type: "U",
              bank_id: user.bank_id,
            };
          }

          const instanceResponse = await postDataWithBody(
            getInstanceUrl,
            payload,
            {}
          );
          if (instanceResponse && instanceResponse[0]) {
            const instance = instanceResponse[0];
            apiEndPointUrl =
              instance.instance_url + "/query-builder-api/execute";
            console.log("Constructed apiEndPointUrl:", apiEndPointUrl);
          }
        }
      } catch (error) {
        console.error(
          "Error fetching instance data for apiEndPointUrl:",
          error
        );
      }

      // Determine role type
      let roleType = "U"; // Default
      const roleName = user.role_name?.toUpperCase();
      if (roleName === "SUPER ADMIN") {
        roleType = "SA";
      } else if (user.is_admin === true) {
        roleType = "HO";
      } else if (roleName === "BRANCH OFFICER") {
        roleType = "BA";
      }

      console.log("User role determined:", roleType);

      let queryBuilderString;
      if (!queryBuilder || queryBuilder === "null") {
        // Fallback to original query if enhanced query building failed
        if (typeof originalQuery.queryBuilder === "string") {
          queryBuilderString = originalQuery.queryBuilder;
        } else {
          queryBuilderString = JSON.stringify(originalQuery.queryBuilder);
        }
        console.log(
          "Using original queryBuilder as fallback:",
          queryBuilderString
        );
      } else if (typeof queryBuilder === "string") {
        queryBuilderString = queryBuilder;
      } else if (typeof queryBuilder === "object") {
        queryBuilderString = JSON.stringify(queryBuilder);
      } else {
        console.error("Invalid queryBuilder format:", queryBuilder);
        return;
      }

      // Build the payload using original query data + additional required fields
      const payload = {
        // Use original query data
        serviceName: originalQuery.serviceName,
        serviceDescription: originalQuery.serviceDescription,
        queryBuilder: queryBuilderString, // This now preserves PRIMARY structure
        created_by: user.id,
        active_status:
          originalQuery.active_status !== undefined
            ? originalQuery.active_status
            : true,
        widgetType: originalQuery.widgetType,

        // Add the required additional fields for SECONDARY query
        duplicateNameAllow: true,
        roleType: roleType,
        bankId: user.bank_id,
        dashboardWidgetId: `WIDGET_${newItem.editId}`,
        queryStage: "SECONDARY", // This marks it as SECONDARY query
        apiEndPointUrl: apiEndPointUrl,

        // Preserve other original fields that might exist
        ...(location.state?.dashboardId && {
          dashboardId: Number(location.state.dashboardId),
        }),
        ...(originalQuery.regionId && { regionId: originalQuery.regionId }),
        ...(originalQuery.parentRegionId && {
          parentRegionId: originalQuery.parentRegionId,
        }),
        ...(originalQuery.adminRole && { adminRole: originalQuery.adminRole }),
      };

      const currentDashboardWidgetId = `WIDGET_${newItem.editId}`;
      const matchingWidgetQuery = SavedAllQueries.find(
        (q) => q.dashboardWidgetId == currentDashboardWidgetId
      );
      if (matchingWidgetQuery && matchingWidgetQuery.id) {
        payload.id = matchingWidgetQuery.id;
        console.log(
          "DashboardWidgetId matches! Adding originalQueryId:",
          matchingWidgetQuery.id
        );
      } else {
        console.log("No matching dashboardWidgetId found or no ID available");
      }

      // Call the API
      const response = await fetch("/nifi/saveServicebuilderAPI", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("API Error Response:", errorText);
        throw new Error(
          `Failed to save widget query: ${response.status} - ${errorText}`
        );
      }

      const data = await response.json();
      console.log("Widget query saved successfully:", data);

      // toast.success("Widget query updated successfully!", {
      //   position: "top-center"
      // });
    } catch (error) {
      console.error("Error saving widget query:", error);
      toast.error(`Failed to save widget query: ${error.message}`, {
        position: "top-center",
      });
    } finally {
      setLoader(false);
    }
  };

  const resetNewItem = () => {
    setNewItem({
      type: "card",
      title: "",
      value: "",
      subtitle: "",
      color: "#ffffff",
      icon: "building",
      chartType: "line",
      data: [],
      showDownloadExcelChart: false,
      showDownloadPDFChart: false,
      pdfChartTitle: "",
      enableChartPDFTitle: false,
      dateFieldsChanged: false,
      xAxisKey: "",
      yAxisKeys: [],
      colors: ["#ffffff", "#10B981"],
      columns: [],
      subColumns: [],
      showDownload: false,
      showPDFDownload: false,
      pdfTitle: "",
      enablePDFTitle: false,
      showBankLogo: false,
      showDateFilter: false,
      showSearch: false,
      isEditing: false,
      editId: null,
      headerColor: "#f9fafb",
      headerTextColor: "#374151",
      subHeaderColor: "#f1f5f9",
      subHeaderTextColor: "#334155",
      fieldConfigs: [], // Add this
      allAvailableFields: [], // Add this
      xAxisOrientation: "horizontal",
      // Add HO_bank_list specific properties
      bankList: [],
      showBankDetails: true,
      bankListLayout: "list",
      maxBanksToShow: 10,
      bankCardColor: "#ffffff",
      branchCardColor: "#ffffff",
      title_color: "#000000",
      title_fontSize: 16,
      title_textAlignment: "left",
    });
  };
  const generateId = () => Math.floor(Math.random() * 1000000);
  const handleEdit = (id, field, value) => {
    if (field === "openEditPopup") {
      const itemToEdit = dashboardItems.find((item) => item.id === id);
      if (itemToEdit) {
        if (itemToEdit.type === "map") {
          // Initialize with existing data sources or empty array
          const dataSources = Array.isArray(itemToEdit.dataSource)
            ? itemToEdit.dataSource
            : itemToEdit.dataSource
            ? [itemToEdit.dataSource]
            : [];

          let fieldsBySource = {};

          // Check if we already have existing fieldsBySource data
          if (
            itemToEdit.fieldsBySource &&
            Object.keys(itemToEdit.fieldsBySource).length > 0
          ) {
            // Use existing fieldsBySource as the base - THIS IS THE KEY FIX
            fieldsBySource = JSON.parse(
              JSON.stringify(itemToEdit.fieldsBySource)
            ); // Deep copy to prevent mutations

            // Ensure all selected data sources have field configurations
            dataSources.forEach((source) => {
              if (!fieldsBySource[source]) {
                // This data source doesn't have field configuration, create it
                const sourceData = dataSourcesForMap[source]?.sampleData || [];
                fieldsBySource[source] = sourceData[0]
                  ? Object.keys(sourceData)
                      .filter((key) => key !== "lat" && key !== "lng")
                      .map((key, index) => ({
                        key,
                        label: key
                          .replace(/([A-Z])/g, " $1")
                          .replace(/^./, (str) => str.toUpperCase()),
                        selected: true,
                        order: index + 1,
                        hidden: key === "bank_id" || key === "branch_id",
                      }))
                  : [];
              }
            });

            // Remove field configurations for data sources that are no longer selected
            Object.keys(fieldsBySource).forEach((source) => {
              if (!dataSources.includes(source)) {
                delete fieldsBySource[source];
              }
            });
          } else {
            // No existing fieldsBySource data, create from scratch
            dataSources.forEach((source) => {
              const sourceData = dataSourcesForMap[source]?.sampleData || [];
              fieldsBySource[source] = sourceData[0]
                ? Object.keys(sourceData)
                    .filter((key) => key !== "lat" && key !== "lng")
                    .map((key, index) => ({
                      key,
                      label: key
                        .replace(/([A-Z])/g, " $1")
                        .replace(/^./, (str) => str.toUpperCase()),
                      selected: true,
                      order: index + 1,
                      hidden: key === "bank_id" || key === "branch_id",
                    }))
                : [];
            });
          }

          setNewItem({
            ...itemToEdit,
            isEditing: true,
            editId: id,
            dataSource: dataSources,
            fieldsBySource: fieldsBySource,
            data: itemToEdit.data,
            showMarkers: itemToEdit.showMarkers !== false,
            showHeatmap: itemToEdit.showHeatmap || false,
            clusterMarkers: itemToEdit.clusterMarkers || false,
            xAxisOrientation: itemToEdit.xAxisOrientation || "horizontal",
            xAxisLabelFontSize: itemToEdit.xAxisLabelFontSize || 14,
            yAxisLabelFontSize: itemToEdit.yAxisLabelFontSize || 14,
            legendFontSize: itemToEdit.legendFontSize || 14,
            showDownloadExcelChart: itemToEdit.showDownloadExcelChart || false,
            showDownloadPDFChart: itemToEdit.showDownloadPDFChart || false,
            showBankLogo: itemToEdit.showBankLogo || false,
            pdfChartTitle: itemToEdit.pdfChartTitle || "",
            enableChartPDFTitle: itemToEdit.enableChartPDFTitle || false,
            fromDate: itemToEdit.fromDate || "",
            toDate: itemToEdit.toDate || "",
            dateFieldsChanged: false,
            selectedDateField: itemToEdit.selectedDateField || "",
          });
          setShowEditPopup(true);
          return;
        }
        // let initialDateRange = { from: null, to: null };
        if (
          itemToEdit.type === "chart" &&
          itemToEdit.data &&
          itemToEdit.data.length > 0
        ) {
          try {
            // Load previously applied date range if it exists
            let initialDateRange = { from: null, to: null };
            if (itemToEdit.appliedDateRange) {
              initialDateRange = {
                from: itemToEdit.appliedDateRange.from,
                to: itemToEdit.appliedDateRange.to,
              };
            }

            // Set the date range state with the previously applied values
            setDateRange(initialDateRange);

            if (itemToEdit.data && itemToEdit.data.length > 0) {
              const firstDataItem = itemToEdit.data[0];
              const xAxisOpts = [];
              const yAxisOpts = [];

              Object.keys(firstDataItem).forEach((key) => {
                const value = firstDataItem[key];
                if (typeof value === "string") {
                  xAxisOpts.push(key);
                } else if (typeof value === "number") {
                  yAxisOpts.push(key);
                }
              });

              // Set the axis options state
              setXAxisOptions(xAxisOpts);
              setYAxisOptions(yAxisOpts);
              // setAvailableFields([...xAxisOpts, ...yAxisOpts]); // If you use this state
            }
          } catch (e) {
            console.error("Error processing dates:", e);
          }
        }

        if (itemToEdit.type === "chart") {
          console.log("[Edit] opening chart", itemToEdit);

          // 1. get the ORIGINAL dataset (never filtered)
          const originalData = itemToEdit.data || [];

          // 2. store both `data` (we show this in the builder) and `originalData`
          //    so that the date picker can always start from the raw set
          setNewItem({
            ...itemToEdit,
            isEditing: true,
            editId: id,
            data: originalData, // <- show untouched data in the popup
            originalData: originalData, // <- remember it for the next edit
            dataSource: itemToEdit.dataSource || "",
            /* keep every other chart-specific prop you already have */
            chartType: itemToEdit.chartType || "line",
            xAxisKey: itemToEdit.xAxisKey || "",
            yAxisKeys: itemToEdit.yAxisKeys || [],
            yAxisLabels: itemToEdit.yAxisLabels || [],
            fromDate: itemToEdit.fromDate || "",
            toDate: itemToEdit.toDate || "",
            dateFieldsChanged: false,
            selectedDateField: itemToEdit.selectedDateField || "",
            pieColors: itemToEdit.pieColors || [],
            colors: (() => {
              const need = (itemToEdit.yAxisKeys || []).length;
              const have = (itemToEdit.colors || []).length;
              if (have >= need) return itemToEdit.colors;
              return [
                ...itemToEdit.colors,
                ...Array(need - have)
                  .fill()
                  .map((_, i) => chartColors[(have + i) % chartColors.length]),
              ];
            })(),
            gridLines:
              itemToEdit.gridLines !== undefined ? itemToEdit.gridLines : true,
            curveType: itemToEdit.curveType || "monotone",
            legendPosition: itemToEdit.legendPosition || "bottom",
            animation: itemToEdit.animation || "default",
            ...(itemToEdit.chartType === "bar" && {
              layout: itemToEdit.layout || "horizontal",
              barWidth: itemToEdit.barWidth || 30,
            }),
            ...(itemToEdit.chartType === "pie" && {
              innerRadius: itemToEdit.innerRadius ?? 0,
              showLabels: itemToEdit.showLabels ?? true,
            }),
            ...(itemToEdit.chartType === "area" && {
              areaOpacity: itemToEdit.areaOpacity ?? 50,
              showGradient: itemToEdit.showGradient ?? false,
            }),
          });

          setShowEditPopup(true);
          return; // very important – prevents the code from falling
          // into the table branch for charts
        }

        if (itemToEdit.type === "map") {
          // Initialize with existing data sources or empty array
          const dataSources = itemToEdit.dataSource || [];
          const fieldsBySource = {};

          // Initialize fields for each data source
          dataSources.forEach((source) => {
            const sourceData = dataSourcesForMap[source]?.sampleData || [];
            fieldsBySource[source] = sourceData[0]
              ? Object.keys(sourceData[0]).map((key) => ({
                  key,
                  label: key
                    .replace(/([A-Z])/g, " $1")
                    .replace(/^./, (str) => str.toUpperCase()),
                  selected: true,
                }))
              : [];
          });

          setNewItem({
            ...itemToEdit,
            isEditing: true,
            editId: id,
            dataSource: dataSources,
            fieldsBySource: fieldsBySource,
            showMarkers: itemToEdit.showMarkers !== false,
            showHeatmap: itemToEdit.showHeatmap || false,
            clusterMarkers: itemToEdit.clusterMarkers || false,
            colors:
              itemToEdit.colors &&
              itemToEdit.colors.length >= itemToEdit.yAxisKeys.length
                ? itemToEdit.colors
                : [
                    ...itemToEdit.colors,
                    ...Array(
                      Math.max(
                        0,
                        itemToEdit.yAxisKeys.length - itemToEdit.colors.length
                      )
                    )
                      .fill()
                      .map(
                        (_, i) =>
                          chartColors[
                            (itemToEdit.colors.length + i) % chartColors.length
                          ]
                      ),
                  ],
          });
          setShowEditPopup(true);
          return;
        }
        if (itemToEdit.type === "table") {
          console.log("Editing table item:", itemToEdit); // Debug log

          // Get the data source configuration
          const sourceConfig = itemToEdit.dataSource
            ? dataSourcesForTable[itemToEdit.dataSource]
            : null;

          // **ENHANCED: Ensure we have the original unfiltered data**
          let originalData = [];
          if (sourceConfig && sourceConfig.data) {
            originalData = sourceConfig.data;
          } else if (itemToEdit.originalData) {
            // Use stored original data if available
            originalData = itemToEdit.originalData;
          } else if (itemToEdit.data) {
            // Fallback to current data (might be filtered)
            originalData = itemToEdit.data;
          }

          // **ADD THIS: Update dataSourcesForTable with original data if missing**
          if (
            itemToEdit.dataSource &&
            !sourceConfig &&
            originalData.length > 0
          ) {
            setDataSourcesForTable((prev) => ({
              ...prev,
              [itemToEdit.dataSource]: {
                data: originalData,
                columns: itemToEdit.columns?.map((col) => col.key) || [],
                defaultTableType: itemToEdit.tableType || "basic",
              },
            }));
          }

          let columnsWithSelection = [];
          let subColumnsWithSelection = [];

          // If the item already has columns saved, use them directly
          if (itemToEdit.columns && itemToEdit.columns.length > 0) {
            columnsWithSelection = itemToEdit.columns.map((col) => ({
              key: col.key || "unknown",
              label: col.label || col.key || "Unknown Column",
              type: col.type || "text",
              selected: col.selected !== false,
              order: col.order || 1,
            }));
          }
          // If no saved columns but we have source config, create from source
          else if (sourceConfig && sourceConfig.columns) {
            columnsWithSelection = sourceConfig.columns.map((col, index) => ({
              key: col,
              label: col
                .replace(/([A-Z])/g, " $1")
                .replace(/^./, (str) => str.toUpperCase()),
              type: "text",
              selected: true,
              order: index + 1,
            }));
          }

          // Handle sub-columns for expandable tables
          if (itemToEdit.tableType === "expandable") {
            if (itemToEdit.subColumns && itemToEdit.subColumns.length > 0) {
              subColumnsWithSelection = itemToEdit.subColumns.map((col) => ({
                key: col.key || "unknown",
                label: col.label || col.key || "Unknown Column",
                type: col.type || "text",
                selected: col.selected !== false,
                order: col.order || 1,
              }));
            } else if (sourceConfig && sourceConfig.columns) {
              subColumnsWithSelection = sourceConfig.columns.map(
                (col, index) => ({
                  key: col,
                  label: col
                    .replace(/([A-Z])/g, " $1")
                    .replace(/^./, (str) => str.toUpperCase()),
                  type: "text",
                  selected: true,
                  order: index + 1,
                })
              );
            }
          }

          setNewItem({
            ...itemToEdit,
            isEditing: true,
            editId: id,
            columns: columnsWithSelection,
            subColumns: subColumnsWithSelection,
            data: itemToEdit.data || [],
            originalData: originalData,
            dataSource: itemToEdit.dataSource || "",
            tableType: itemToEdit.tableType || "basic",
            // Preserve all other existing properties
            showDownload: itemToEdit.showDownload || false,
            showPDFDownload: itemToEdit.showPDFDownload || false,
            pdfTitle: itemToEdit.pdfTitle || "",
            enablePDFTitle: itemToEdit.enablePDFTitle || false,
            showSearch: itemToEdit.showSearch || false,
            showPagination: itemToEdit.showPagination || false,
            enableSorting: itemToEdit.enableSorting || false,
            showDateFilter: itemToEdit.showDateFilter || false,
            headerColor: itemToEdit.headerColor || "#f9fafb",
            headerTextColor: itemToEdit.headerTextColor || "#374151",
            subHeaderColor: itemToEdit.subHeaderColor || "#f1f5f9",
            subHeaderTextColor: itemToEdit.subHeaderTextColor || "#334155",
            fromDate: itemToEdit.fromDate || "",
            toDate: itemToEdit.toDate || "",
            dateFieldsChanged: false,
            selectedDateField: itemToEdit.selectedDateField || "",
          });

          console.log("Columns being set:", columnsWithSelection); // Debug log
        }
        if (itemToEdit.type === "card") {
          let fieldConfigs = [];
          let allAvailableFields = [];

          if (itemToEdit.dataSource) {
            const matchedQuery = savedQueries.find(
              (q) => q.serviceName === itemToEdit.dataSource
            );
            const matchedId = savedQueries.find((q) => q.id);

            console.log(matchedId, "matchedid");

            if (matchedQuery) {
              (async () => {
                try {
                  const queryObject =
                    typeof matchedQuery.queryBuilder === "string"
                      ? JSON.parse(matchedQuery.queryBuilder)
                      : matchedQuery.queryBuilder;

                  const response = await fetch("/nifi/fetchQuery", {
                    method: "POST",
                    headers: {
                      "Content-Type": "application/json",
                    },
                    body: JSON.stringify(queryObject),
                  });

                  const result = await response.json();
                  console.log("Fresh data for card edit:", result);

                  if (result.data && result.data.length > 0) {
                    allAvailableFields = Object.keys(result.data[0] || {});

                    // Define ID fields to exclude from configuration UI
                    const hiddenIdFields = [
                      "bank_id",
                      "branchdetails_id",
                      "branch_id",
                    ];

                    // Filter fields for configuration UI (exclude ID fields)
                    const configurableFields = allAvailableFields.filter(
                      (field) => !hiddenIdFields.includes(field.toLowerCase())
                    );

                    // Use cardStyles and fieldsConfig from existing item
                    const existingCardStyles = itemToEdit.cardStyles || {};
                    const existingFieldsConfig = itemToEdit.fieldsConfig || [];

                    // Create field configs based on existing data (only for configurable fields)
                    fieldConfigs = configurableFields.map((field) => {
                      const existingFieldConfig = existingFieldsConfig.find(
                        (config) => config.fieldName === field
                      );
                      const isEnabled = existingFieldConfig
                        ? existingFieldConfig.enabled
                        : existingCardStyles[`${field}_color`] !== undefined;

                      return {
                        fieldName: field,
                        color:
                          existingCardStyles[`${field}_color`] || "#000000",
                        fontSize: (() => {
                          const size = existingCardStyles[`${field}_fontSize`];
                          if (size === 12) return "small";
                          if (size === 16) return "medium";
                          if (size === 20) return "large";
                          if (size === 24) return "extra-large";
                          return "custom";
                        })(),
                        fontSizeValue:
                          existingCardStyles[`${field}_fontSize`] || 12,
                        textAlignment:
                          existingCardStyles[`${field}_textAlignment`] ||
                          "left",
                        enabled: isEnabled,
                      };
                    });

                    // Create fieldsConfig based on fieldConfigs (only for configurable fields)
                    const fieldsConfig = fieldConfigs.map((config) => ({
                      fieldName: config.fieldName,
                      enabled: config.enabled,
                    }));

                    // Filter data based on enabled fields + always include ID fields
                    const enabledFields = fieldsConfig
                      .filter((config) => config.enabled)
                      .map((config) => config.fieldName);

                    // Include ID fields in data even if not in configuration
                    const idFields = allAvailableFields.filter((field) =>
                      hiddenIdFields.includes(field.toLowerCase())
                    );
                    const allIncludedFields = [...enabledFields, ...idFields];

                    const filteredData = result.data.map((item) => {
                      const filteredItem = {};
                      allIncludedFields.forEach((field) => {
                        if (item.hasOwnProperty(field)) {
                          filteredItem[field] = item[field];
                        }
                      });
                      return filteredItem;
                    });

                    setNewItem({
                      ...itemToEdit,
                      isEditing: true,
                      editId: id,
                      dataSource: itemToEdit.dataSource,
                      data: filteredData, // Pure data array
                      cardStyles: existingCardStyles, // Separate styles object
                      fieldsConfig: fieldsConfig, // Field visibility config (only configurable fields)
                      dataSourceConfig: {
                        data: filteredData,
                        originalData: result.data,
                        availableFields: allAvailableFields,
                        configurableFields: configurableFields, // Store configurable fields separately
                        hiddenIdFields: idFields, // Store hidden ID fields
                        serviceName: itemToEdit.dataSource,
                      },
                      fieldConfigs: fieldConfigs,
                      allAvailableFields: allAvailableFields,
                      valueKey: itemToEdit.valueKey || "",
                      value: itemToEdit.value || "",
                      subtitle: itemToEdit.subtitle || "",
                      color: itemToEdit.color || "#ffffff",
                      icon: itemToEdit.icon || "FiDollarSign",
                      widgetSize: itemToEdit.widgetSize || "medium",
                      textColor: itemToEdit.textColor || "#1f2937",
                      fontSize: itemToEdit.fontSize || "medium",
                      customFontSize: itemToEdit.customFontSize || null,
                      textAlignment: itemToEdit.textAlignment || "left",
                      trend: itemToEdit.trend || null,
                      trendValue: itemToEdit.trendValue || null,
                      trendColor: itemToEdit.trendColor || null,
                      fromDate: itemToEdit.fromDate || "",
                      toDate: itemToEdit.toDate || "",
                      dateFieldsChanged: false,
                      selectedDateField: itemToEdit.selectedDateField || "",
                    });

                    setShowEditPopup(true);
                  }
                } catch (error) {
                  console.error(
                    "Error fetching fresh data for card edit:",
                    error
                  );

                  setNewItem({
                    ...itemToEdit,
                    isEditing: true,
                    editId: id,
                    dataSource: itemToEdit.dataSource || "",
                    dataSourceConfig: itemToEdit.dataSourceConfig || "",
                    cardStyles: itemToEdit.cardStyles || {}, // Preserve existing styles
                    fieldsConfig: itemToEdit.fieldsConfig || [], // Preserve existing field config
                    valueKey: itemToEdit.valueKey || "",
                    value: itemToEdit.value || "",
                    subtitle: itemToEdit.subtitle || "",
                    color: itemToEdit.color || "#ffffff",
                    icon: itemToEdit.icon || "FiDollarSign",
                    widgetSize: itemToEdit.widgetSize || "medium",
                    textColor: itemToEdit.textColor || "#1f2937",
                    fontSize: itemToEdit.fontSize || "medium",
                    customFontSize: itemToEdit.customFontSize || null,
                    textAlignment: itemToEdit.textAlignment || "left",
                    trend: itemToEdit.trend || null,
                    trendValue: itemToEdit.trendValue || null,
                    trendColor: itemToEdit.trendColor || null,
                    fromDate: itemToEdit.fromDate || "",
                    toDate: itemToEdit.toDate || "",
                  });
                  setShowEditPopup(true);
                }
              })();
              return;
            }
          }

          setNewItem({
            ...itemToEdit,
            isEditing: true,
            editId: id,
            dataSource: itemToEdit.dataSource || "",
            cardStyles: itemToEdit.cardStyles || {},
            fieldsConfig: itemToEdit.fieldsConfig || [], // Preserve existing field config
            valueKey: itemToEdit.valueKey || "",
            value: itemToEdit.value || "",
            subtitle: itemToEdit.subtitle || "",
            color: itemToEdit.color || "#ffffff",
            icon: itemToEdit.icon || "FiDollarSign",
            widgetSize: itemToEdit.widgetSize || "medium",
            textColor: itemToEdit.textColor || "#1f2937",
            fontSize: itemToEdit.fontSize || "medium",
            customFontSize: itemToEdit.customFontSize || null,
            textAlignment: itemToEdit.textAlignment || "left",
            trend: itemToEdit.trend || null,
            trendValue: itemToEdit.trendValue || null,
            trendColor: itemToEdit.trendColor || null,
            fromDate: itemToEdit.fromDate || "",
            toDate: itemToEdit.toDate || "",
          });
          setShowEditPopup(true);
        }

        if (itemToEdit.type === "HO_bank_list") {
          console.log("Editing HO_bank_list item:", itemToEdit);
          let fieldConfigs = [];
          let allAvailableFields = [];

          if (itemToEdit.dataSource) {
            const matchedQuery = savedQueries.find(
              (q) => q.serviceName === itemToEdit.dataSource
            );

            if (matchedQuery) {
              // Make async call to fetch fresh data
              (async () => {
                try {
                  const queryObject =
                    typeof matchedQuery.queryBuilder === "string"
                      ? JSON.parse(matchedQuery.queryBuilder)
                      : matchedQuery.queryBuilder;

                  const response = await fetch("/nifi/fetchQuery", {
                    method: "POST",
                    headers: {
                      "Content-Type": "application/json",
                    },
                    body: JSON.stringify(queryObject),
                  });

                  const result = await response.json();
                  console.log("Fresh data for HO_bank_list edit:", result);

                  if (result.data && result.data.length > 0) {
                    allAvailableFields = Object.keys(result.data[0] || {});

                    // Use cardStyles and fieldsConfig from existing item
                    const existingCardStyles = itemToEdit.cardStyles || {};
                    const existingFieldsConfig = itemToEdit.fieldsConfig || [];

                    // Separate bank and branch fields (excluding ID fields from UI)
                    const bankFields = allAvailableFields.filter(
                      (field) =>
                        field.startsWith("bank_") && field !== "bank_id"
                    );
                    const branchFields = allAvailableFields.filter(
                      (field) =>
                        field.startsWith("branch") &&
                        field !== "branchdetails_id" &&
                        field !== "branch_id"
                    );

                    // Create field configs for bank fields based on existing data
                    const bankFieldConfigs = bankFields.map((field) => {
                      const existingFieldConfig = existingFieldsConfig.find(
                        (config) => config.fieldName === field
                      );
                      const isEnabled = existingFieldConfig
                        ? existingFieldConfig.enabled
                        : existingCardStyles[`${field}_color`] !== undefined;

                      return {
                        fieldName: field,
                        color:
                          existingCardStyles[`${field}_color`] || "#000000",
                        fontSize: (() => {
                          const size = existingCardStyles[`${field}_fontSize`];
                          if (size === 12) return "small";
                          if (size === 16) return "medium";
                          if (size === 20) return "large";
                          if (size === 24) return "extra-large";
                          return "custom";
                        })(),
                        fontSizeValue:
                          existingCardStyles[`${field}_fontSize`] || 12,
                        textAlignment:
                          existingCardStyles[`${field}_textAlignment`] ||
                          "left",
                        enabled: isEnabled,
                        category: "bank",
                      };
                    });

                    // Create field configs for branch fields based on existing data
                    const branchFieldConfigs = branchFields.map((field) => {
                      const existingFieldConfig = existingFieldsConfig.find(
                        (config) => config.fieldName === field
                      );
                      const isEnabled = existingFieldConfig
                        ? existingFieldConfig.enabled
                        : existingCardStyles[`${field}_color`] !== undefined;

                      return {
                        fieldName: field,
                        color:
                          existingCardStyles[`${field}_color`] || "#000000",
                        fontSize: (() => {
                          const size = existingCardStyles[`${field}_fontSize`];
                          if (size === 12) return "small";
                          if (size === 16) return "medium";
                          if (size === 20) return "large";
                          if (size === 24) return "extra-large";
                          return "custom";
                        })(),
                        fontSizeValue:
                          existingCardStyles[`${field}_fontSize`] || 12,
                        textAlignment:
                          existingCardStyles[`${field}_textAlignment`] ||
                          "left",
                        enabled: isEnabled,
                        category: "branch",
                      };
                    });

                    // Combine all field configs
                    fieldConfigs = [...bankFieldConfigs, ...branchFieldConfigs];

                    // Create fieldsConfig based on fieldConfigs
                    const fieldsConfig = fieldConfigs.map((config) => ({
                      fieldName: config.fieldName,
                      enabled: config.enabled,
                    }));

                    // Filter data based on enabled fields
                    const enabledFields = fieldsConfig
                      .filter((config) => config.enabled)
                      .map((config) => config.fieldName);

                    // Include ID fields in data (needed for functionality)
                    const fieldsToIncludeInData = [...enabledFields];
                    if (allAvailableFields.includes("bank_id")) {
                      fieldsToIncludeInData.push("bank_id");
                    }
                    if (allAvailableFields.includes("branch_id")) {
                      fieldsToIncludeInData.push("branch_id");
                    }
                    if (allAvailableFields.includes("branchdetails_id")) {
                      fieldsToIncludeInData.push("branchdetails_id");
                    }

                    const filteredData = result.data.map((item) => {
                      const filteredItem = {};
                      fieldsToIncludeInData.forEach((field) => {
                        if (item.hasOwnProperty(field)) {
                          filteredItem[field] = item[field];
                        }
                      });
                      return filteredItem;
                    });

                    setNewItem({
                      ...itemToEdit,
                      isEditing: true,
                      editId: id,
                      dataSource: itemToEdit.dataSource,
                      data: filteredData, // Pure data array
                      cardStyles: existingCardStyles, // Separate styles object
                      fieldsConfig: fieldsConfig, // Field visibility config
                      dataSourceConfig: {
                        data: filteredData,
                        originalData: result.data,
                        availableFields: allAvailableFields,
                        serviceName: itemToEdit.dataSource,
                      },
                      fieldConfigs: fieldConfigs,
                      allAvailableFields: allAvailableFields,
                      valueKey: itemToEdit.valueKey || "",
                      value: itemToEdit.value || "",
                      subtitle: itemToEdit.subtitle || "",
                      color: itemToEdit.color || "#ffffff",
                      icon: itemToEdit.icon || "FiBank",
                      widgetSize: itemToEdit.widgetSize || "medium",
                      textColor: itemToEdit.textColor || "#1f2937",
                      fontSize: itemToEdit.fontSize || "medium",
                      customFontSize: itemToEdit.customFontSize || null,
                      textAlignment: itemToEdit.textAlignment || "left",
                      trend: itemToEdit.trend || null,
                      trendValue: itemToEdit.trendValue || null,
                      trendColor: itemToEdit.trendColor || null,
                      fromDate: itemToEdit.fromDate || "",
                      toDate: itemToEdit.toDate || "",
                      // HO_bank_list specific properties
                      bankList: itemToEdit.bankList || [],
                      showBankDetails: itemToEdit.showBankDetails ?? true,
                      bankListLayout: itemToEdit.bankListLayout || "list",
                      maxBanksToShow: itemToEdit.maxBanksToShow || 10,
                      bankCardColor:
                        existingCardStyles.bank_card_color ||
                        itemToEdit.bankCardColor ||
                        "#ffffff",
                      branchCardColor:
                        existingCardStyles.branch_card_color ||
                        itemToEdit.branchCardColor ||
                        "#ffffff",
                      expand_color:
                        existingCardStyles.expand_color ||
                        itemToEdit.expand_color ||
                        "#000000",
                    });

                    // Show popup after successful API call
                    setShowEditPopup(true);
                  }
                } catch (error) {
                  console.error(
                    "Error fetching fresh data for HO_bank_list edit:",
                    error
                  );

                  // Fallback - still show edit popup with existing data
                  setNewItem({
                    ...itemToEdit,
                    isEditing: true,
                    editId: id,
                    dataSource: itemToEdit.dataSource || "",
                    dataSourceConfig: itemToEdit.dataSourceConfig || "",
                    cardStyles: itemToEdit.cardStyles || {}, // Preserve existing styles
                    fieldsConfig: itemToEdit.fieldsConfig || [], // Preserve existing field config
                    valueKey: itemToEdit.valueKey || "",
                    value: itemToEdit.value || "",
                    subtitle: itemToEdit.subtitle || "",
                    color: itemToEdit.color || "#ffffff",
                    icon: itemToEdit.icon || "FiBank",
                    widgetSize: itemToEdit.widgetSize || "medium",
                    textColor: itemToEdit.textColor || "#1f2937",
                    fontSize: itemToEdit.fontSize || "medium",
                    customFontSize: itemToEdit.customFontSize || null,
                    textAlignment: itemToEdit.textAlignment || "left",
                    trend: itemToEdit.trend || null,
                    trendValue: itemToEdit.trendValue || null,
                    trendColor: itemToEdit.trendColor || null,
                    fromDate: itemToEdit.fromDate || "",
                    toDate: itemToEdit.toDate || "",
                    // HO_bank_list specific properties
                    bankList: itemToEdit.bankList || [],
                    showBankDetails: itemToEdit.showBankDetails ?? true,
                    bankListLayout: itemToEdit.bankListLayout || "list",
                    maxBanksToShow: itemToEdit.maxBanksToShow || 10,
                    bankCardColor: itemToEdit.bankCardColor || "#ffffff",
                    branchCardColor: itemToEdit.branchCardColor || "#ffffff",
                    expand_color: itemToEdit.expand_color || "#000000",
                  });
                  setShowEditPopup(true);
                }
              })();

              // Return early to prevent execution of the else block
              return;
            }
          }

          // Handle case when no dataSource or no matched query
          setNewItem({
            ...itemToEdit,
            isEditing: true,
            editId: id,
            dataSource: itemToEdit.dataSource || "",
            cardStyles: itemToEdit.cardStyles || {},
            fieldsConfig: itemToEdit.fieldsConfig || [],
            valueKey: itemToEdit.valueKey || "",
            value: itemToEdit.value || "",
            subtitle: itemToEdit.subtitle || "",
            color: itemToEdit.color || "#ffffff",
            icon: itemToEdit.icon || "FiBank",
            widgetSize: itemToEdit.widgetSize || "medium",
            textColor: itemToEdit.textColor || "#1f2937",
            fontSize: itemToEdit.fontSize || "medium",
            customFontSize: itemToEdit.customFontSize || null,
            textAlignment: itemToEdit.textAlignment || "left",
            trend: itemToEdit.trend || null,
            trendValue: itemToEdit.trendValue || null,
            trendColor: itemToEdit.trendColor || null,
            fromDate: itemToEdit.fromDate || "",
            toDate: itemToEdit.toDate || "",
            // HO_bank_list specific properties
            bankList: itemToEdit.bankList || [],
            showBankDetails: itemToEdit.showBankDetails ?? true,
            bankListLayout: itemToEdit.bankListLayout || "list",
            maxBanksToShow: itemToEdit.maxBanksToShow || 10,
            bankCardColor: itemToEdit.bankCardColor || "#ffffff",
            branchCardColor: itemToEdit.branchCardColor || "#ffffff",
            expand_color: itemToEdit.expand_color || "#000000",
          });
          setShowEditPopup(true);
          return;
        } else {
          setNewItem({
            ...itemToEdit,
            isEditing: true,
            editId: id,
            dataSource: itemToEdit.dataSource || "",
          });
        }
        setShowEditPopup(true);
      }
      return;
    }

    setDashboardItems((prevItems) =>
      prevItems.map((item) =>
        item.id === id ? { ...item, [field]: value } : item
      )
    );
  };
  const handleDelete = (id) => {
    const itemToRemove = dashboardItems.find((item) => item.id === id);
    if (itemToRemove) {
      setDashboardItems((prev) => prev.filter((item) => item.id !== id));
      setRemovedItems((prev) => [...prev, itemToRemove]);
      setShowRemovedSection(true); // Auto-show removed section
    }
  };

  // Enhanced permanent delete function
  const handlePermanentDelete = (id) => {
    if (id === "all") {
      // Clear all removed items
      setRemovedItems([]);
      setShowRemovedSection(false);
    } else {
      // Remove single item permanently
      setRemovedItems((prev) => prev.filter((item) => item.id !== id));

      // Hide removed section if no more items
      if (removedItems.length === 1) {
        setShowRemovedSection(false);
      }
    }
  };

  // Enhanced restore function (if not already implemented)
  const handleRestore = (id) => {
    const itemToRestore = removedItems.find((item) => item.id === id);
    if (itemToRestore) {
      setRemovedItems((prev) => prev.filter((item) => item.id !== id));
      setDashboardItems((prev) => [...prev, itemToRestore]);

      // Hide removed section if no more items
      if (removedItems.length === 1) {
        setShowRemovedSection(false);
      }
    }
  };

  const handleDuplicate = (id) => {
    const itemToDuplicate = dashboardItems.find((item) => item.id === id);
    if (itemToDuplicate) {
      const newItem = {
        ...itemToDuplicate,
        id: generateId(),
        title: `${itemToDuplicate.title} (Copy)`,
      };
      setDashboardItems((prev) => [...prev, newItem]);
    }
  };

  // First, get your filtered dashboard items based on search
  const filteredDashboardItems = useMemo(() => {
    if (!searchTerm.trim()) {
      return dashboardItems;
    }

    console.log("dashboardItems", dashboardItems);

    return dashboardItems.filter(
      (item) =>
        item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.subtitle &&
          item.subtitle.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.type &&
          item.type.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }, [dashboardItems, searchTerm]);

  // Then apply your data update logic to the searched items
  const filteredItems = filteredDashboardItems.map((item) => {
    if (item.dataSource === newItem.dataSource) {
      return {
        ...item,
        data: newItem.data,
      };
    }
    return item;
  });

  console.log(
    "Selected District from location:",
    location.state?.dashboardData
  );

  const handleAddYAxisKey = () => {
    setNewItem({
      ...newItem,
      yAxisKeys: [...newItem.yAxisKeys, ""],
    });
  };
  const handleRemoveYAxisKey = (index) => {
    const updatedYAxisKeys = [...newItem.yAxisKeys];
    const updatedYAxisLabels = [...(newItem.yAxisLabels || [])];
    updatedYAxisKeys.splice(index, 1);
    // Also remove the corresponding label at the same index
    updatedYAxisLabels.splice(index, 1);
    setNewItem({
      ...newItem,
      yAxisKeys: updatedYAxisKeys,
      yAxisLabels: updatedYAxisLabels, // Add this line
    });
  };
  const handleYAxisKeyChange = (index, value) => {
    const updatedYAxisKeys = [...newItem.yAxisKeys];
    updatedYAxisKeys[index] = value;
    setNewItem({
      ...newItem,
      yAxisKeys: updatedYAxisKeys,
    });
  };

  // Date validation function for Apply button - requires BOTH dates
  const isDateFilterValid = () => {
    // Must have BOTH fromDate AND toDate selected
    if (!fromDate || !toDate) return false;

    const today = new Date().toISOString().split("T")[0];
    const fromDateStr = fromDate.toISOString().split("T")[0];
    const toDateStr = toDate.toISOString().split("T")[0];

    // Check if fromDate is in the future
    if (fromDateStr > today) return false;

    // Check if toDate is in the future
    if (toDateStr > today) return false;

    // Check if toDate is before fromDate
    if (toDateStr < fromDateStr) return false;

    // All validations passed and both dates are selected
    return true;
  };
  console.log(dashboardConfig.selectedBankIds, "bankkkkkkkkkkkkkkkkkkk");
  console.log("selectedBankIds:", dashboardConfig.selectedBankIds);
  console.log("length:", dashboardConfig.selectedBankIds?.length);
  console.log("userRole:", userRole);

  // console.log(selectedBankIds,'bankkkkkkkkkkkkkkkkkkksssssssss')
  console.log(userlist, "dashboardStatus");
  console.log("edit dashboardConfig", dashboardConfig);
  return (
    <DndProvider backend={HTML5Backend}>
      <Loader
        show={loader}
        type="body"
        stack="vertical"
        message="Loading Data"
      />
      <div
        style={{
          minHeight: "100vh",
          //  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
          fontSize: "14px",
          position: "relative",
        }}
      >
        {/* Glassmorphism overlay */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            //  background: "rgba(255, 255, 255, 0.1)",
            backdropFilter: "blur(10px)",
            zIndex: 0,
          }}
        />
        {/* Admin Controls Section - Top Bar */}
        {!isViewMode && (
          <div
            style={{
              background: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(20px)",
              borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
              padding: "26px 36px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              boxShadow: "0 0px 10px rgba(0, 0, 0, 0.1)",
              position: "relative",
              zIndex: 0,
              borderRadius: "20px",
              marginLeft: "32px",
              marginRight: "32px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
              {/* Back Navigation Button */}
              {!isViewMode && (
                <>
                  <button
                    onClick={() => {
                      window.history.back();
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "10px",
                      background: "linear-gradient(145deg, #f8fafc, #e2e8f0)",
                      border: "1px solid rgba(255, 255, 255, 0.3)",
                      borderRadius: "12px",
                      cursor: "pointer",
                      color: "#334155",
                      // boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                      // transition: "all 0.3s ease",
                      minWidth: "40px",
                      height: "40px",
                    }}
                    // onMouseEnter={(e) => {
                    //   e.target.style.transform = "translateX(-2px)";
                    //   e.target.style.boxShadow = "0 6px 16px rgba(0, 0, 0, 0.15)";
                    // }}
                    // onMouseLeave={(e) => {
                    //   e.target.style.transform = "translateX(0)";
                    //   e.target.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.1)";
                    // }}
                  >
                    <ArrowLeft size={18} />
                  </button>
                  <div
                    style={{
                      display: "flex",
                      gap: "16px",
                      alignItems: "center",
                    }}
                  >
                    {/* Capture Screenshot Button */}
                    <button
                      onClick={async () => {
                        const container = document.getElementById(
                          "dashboard-container"
                        );
                        if (container) {
                          try {
                            const html2canvas = (await import("html2canvas"))
                              .default;
                            const canvas = await html2canvas(container, {
                              scale: 2,
                              logging: false,
                              useCORS: true,
                            });
                            const link = document.createElement("a");
                            link.download = "dashboard-screenshot.png";
                            link.href = canvas.toDataURL("image/png");
                            link.click();
                          } catch (error) {
                            console.error("Error capturing screenshot:", error);
                          }
                        }
                      }}
                      style={{
                        padding: "10px 16px",
                        background: "linear-gradient(145deg, #f8fafc, #e2e8f0)",
                        color: "#334155",
                        border: "1px solid rgba(255, 255, 255, 0.3)",
                        borderRadius: "12px",
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: "500",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        // boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                        // transition: "all 0.3s ease",
                      }}
                    >
                      <Camera size={16} />
                      Capture
                    </button>
                  </div>
                </>
              )}
            </div>

            {!isViewMode && (
              <div
                style={{
                  width: "70%",
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <button
                  onClick={handleEditDashboard}
                  style={{
                    padding: "10px 20px",
                    background: "linear-gradient(145deg, #1a7a73, #1a7a73)", // blue
                    color: "white",
                    border: "none",
                    borderRadius: "12px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "600",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    boxShadow: "0 4px 16px rgba(59, 130, 246, 0.3)",
                    transition: "all 0.3s ease",
                  }}
                >
                  <Edit2 size={16} />
                  {/* Edit Dashboard configuration */}
                  Edit configuration
                </button>
                <button
                  onClick={() => {
                    resetNewItem();
                    setShowPopup(true);
                    handleAddWidget();
                  }}
                  style={{
                    padding: "10px 20px",
                    background: "linear-gradient(145deg, #1a7a73, #1a7a73)", // blue
                    color: "white",
                    border: "none",
                    borderRadius: "12px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "600",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    boxShadow: "0 4px 16px rgba(59, 130, 246, 0.3)",
                    transition: "all 0.3s ease",
                  }}
                >
                  <PlusCircle size={16} />
                  Add Widget
                </button>
                <button
                  onClick={() => saveDashboard("Draft", setIsSavingDraft)}
                  disabled={isSavingDashboard}
                  style={{
                    padding: "10px 20px",
                    background: "linear-gradient(145deg, #f59e0b, #d97706)", // amber/yellow-orange
                    color: "white",
                    border: "none",
                    borderRadius: "12px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "600",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    boxShadow: "0 4px 16px rgba(245, 158, 11, 0.3)",
                    transition: "all 0.3s ease",
                  }}
                >
                  <FileText size={16} />
                  {isSavingDraft ? "Saving..." : "Save as Draft"}
                </button>

                <button
                  onClick={() => saveDashboard("Saved", setIsSavingDashboard)}
                  disabled={isSavingDashboard}
                  style={{
                    padding: "10px 20px",
                    background: "linear-gradient(145deg, #10b981, #059669)", // green
                    color: "white",
                    border: "none",
                    borderRadius: "12px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "600",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    boxShadow: "0 4px 16px rgba(16, 185, 129, 0.3)",
                    transition: "all 0.3s ease",
                  }}
                >
                  <SaveAll size={16} />
                  {isSavingDashboard ? "Saving..." : "Save Dashboard"}
                </button>
              </div>
            )}
          </div>
        )}
        {/* Dashboard Content */}
        <div
          style={{
            padding: "32px",
            maxWidth: "1800px",
            margin: "0 auto",
            position: "relative",
            zIndex: 1,
          }}
          id="dashboard-container"
        >
          {/* Dashboard Header */}
          <div
            style={{
              // marginBottom: "32px",
              backgroundColor: "#ffffff",
              padding: "0px 0px",
              borderRadius: "20px",
              position: "relative",
            }}
          >
            <div
              style={{
                marginBottom: "5px",
                backgroundColor: "#ffffff",
                padding: "7px 20px",
                borderRadius: "20px",
                position: "relative",
              }}
            >
              {/* Mode badge positioned in top-right corner */}
              {!isViewMode && (
                <span
                  style={{
                    position: "absolute",
                    top: "15px",
                    right: "20px",
                    fontSize: "12px",
                    fontWeight: "500",
                    padding: "2px 1px",
                    borderRadius: "10px",
                    backgroundColor:
                      currentMode === "Create"
                        ? "#dcfce7"
                        : currentMode === "Edit"
                        ? "#fef3c3"
                        : currentMode === "View"
                        ? "#e0f2fe"
                        : "#f3f4f6",
                    color:
                      currentMode === "Create"
                        ? "#15803d"
                        : currentMode === "Edit"
                        ? "#92400e"
                        : currentMode === "View"
                        ? "#1e3a8a"
                        : "#6b7280",
                    border: `1px solid ${
                      currentMode === "Create"
                        ? "#bbf7d0"
                        : currentMode === "Edit"
                        ? "#fcd34d"
                        : currentMode === "View"
                        ? "#5eead4"
                        : "#d1d5db"
                    }`,
                    minWidth: "110px",
                    textAlign: "center",
                  }}
                >
                  {currentMode} Mode
                </span>
              )}

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                }}
              >
                <div style={{ position: "absolute", left: "20px", top: "0%" }}>
                  {isViewMode && (
                    <>
                      <button
                        onClick={() => {
                          window.history.back();
                        }}
                        style={{
                          marginTop: "20px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: "10px",
                          background:
                            "linear-gradient(145deg, #f8fafc, #e2e8f0)",
                          border: "1px solid rgba(255, 255, 255, 0.3)",
                          borderRadius: "12px",
                          cursor: "pointer",
                          color: "#334155",
                          // boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                          // transition: "all 0.3s ease",
                          minWidth: "40px",
                          height: "40px",
                        }}
                        // onMouseEnter={(e) => {
                        //   e.target.style.transform = "translateX(-2px)";
                        //   e.target.style.boxShadow = "0 6px 16px rgba(0, 0, 0, 0.15)";
                        // }}
                        // onMouseLeave={(e) => {
                        //   e.target.style.transform = "translateX(0)";
                        //   e.target.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.1)";
                        // }}
                      >
                        <ArrowLeft size={18} />
                      </button>
                    </>
                  )}
                </div>

                <div>
                  <h1
                    style={{
                      marginTop: "10px",
                      fontSize: "30px",
                      fontWeight: "bold",
                      color: "#111827",
                      marginBottom: "8px",
                    }}
                  >
                    {dashboardName || "New Dashboard"}
                  </h1>

                  {isViewMode &&
                    dashboardDescription &&
                    dashboardDescription !== "No description available" && (
                      <p style={{ color: "#6b7280", margin: "0 0 0 0" }}>
                        {dashboardDescription}
                      </p>
                    )}

                  {!isViewMode && (
                    <p style={{ color: "#6b7280", margin: "0px" }}>
                      {dashboardDescription}
                    </p>
                  )}
                </div>

                <div style={{ position: "absolute", right: "20px", top: "0%" }}>
                  {isViewMode && (
                    <>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "flex-end",
                          gap: "16px",
                          alignItems: "center",
                        }}
                      >
                        {/* Capture Screenshot Button */}
                        <button
                          onClick={async () => {
                            const container = document.getElementById(
                              "dashboard-container"
                            );
                            if (container) {
                              try {
                                const html2canvas = (
                                  await import("html2canvas")
                                ).default;
                                const canvas = await html2canvas(container, {
                                  scale: 2,
                                  logging: false,
                                  useCORS: true,
                                });
                                const link = document.createElement("a");
                                link.download = "dashboard-screenshot.png";
                                link.href = canvas.toDataURL("image/png");
                                link.click();
                              } catch (error) {
                                console.error(
                                  "Error capturing screenshot:",
                                  error
                                );
                              }
                            }
                          }}
                          style={{
                            marginTop: "20px",
                            padding: "10px 16px",
                            background:
                              "linear-gradient(145deg, #f8fafc, #e2e8f0)",
                            color: "#334155",
                            border: "1px solid rgba(255, 255, 255, 0.3)",
                            borderRadius: "12px",
                            cursor: "pointer",
                            fontSize: "14px",
                            fontWeight: "500",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            // boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                            // transition: "all 0.3s ease",
                          }}
                        >
                          <Camera size={16} />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Dashboard Filters */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "32px",
                flexWrap: "wrap",
                gap: "20px",
                padding: "0px 0px 10px 20px",
                border: "1px solid rgba(255, 255, 255, 0.3)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  gap: "16px",
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                {/* Search - Only show if enabled in config */}
                {dashboardConfig.globalFilters.search && !isViewMode && (
                  <div style={{ position: "relative" }}>
                    <Search
                      size={16}
                      color="#94a3b8"
                      style={{
                        position: "absolute",
                        left: "14px",
                        top: "60%",
                        transform: "translateY(-50%)",
                      }}
                    />
                    <input
                      type="text"
                      placeholder="Search widgets..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{
                        padding: "12px 16px 12px 44px",
                        borderRadius: "12px",
                        border: "1px solid rgba(226, 232, 240, 0.8)",
                        background: "rgba(255, 255, 255, 0.9)",
                        fontSize: "14px",
                        minWidth: "240px",
                        fontWeight: "400",
                        outline: "none",
                        transition: "all 0.3s ease",
                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                        marginTop: "21px",
                      }}
                    />
                  </div>
                )}

                {/* Enhanced Date Range - Only show if enabled in config */}
                {/* {dashboardConfig.globalFilters.dateRange && (
  <div
    style={{
      display: "flex",
      gap: "10px",
      alignItems: "flex-end",
    }}
  >
    <div style={{ minWidth: "150px" }}>
      <label
        style={{
          display: "block",
          marginBottom: "6px",
          fontSize: "12px",
          fontWeight: "500",
          color: "#64748b",
        }}
      >
        From Date
      </label>
      <input
        type="date"
        value={fromDate ? fromDate.toISOString().split('T')[0] : ""}
        onChange={(e) => {
          const selectedDate = e.target.value;
          const today = new Date().toISOString().split("T")[0];
          const selectedDateObj = selectedDate ? new Date(selectedDate) : null;

          // Check if selected date is in the future
          if (selectedDate > today) {
            // Reset to today's date and show warning
            const todayObj = new Date(today);
            setFromDate(todayObj);

            // If today's date is greater than current toDate, clear toDate
            if (toDate && todayObj > toDate) {
              setToDate(null);
            }
            return;
          }

          // Clear any previous errors and update the date
          setFromDate(selectedDateObj);

          // If toDate is before new fromDate, clear it
          if (toDate && selectedDateObj && toDate < selectedDateObj) {
            setToDate(null);
          }
        }}
        max={new Date().toISOString().split("T")[0]}
        style={{
          width: "100%",
          padding: "12px 16px",
          borderRadius: "12px",
          border: fromDate ? "1px solid rgba(34, 197, 94, 0.8)" : "1px solid rgba(226, 232, 240, 0.8)",
          background: "rgba(255, 255, 255, 0.9)",
          fontSize: "14px",
          outline: "none",
          transition: "all 0.3s ease",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
        }}
        onFocus={(e) => (e.target.style.borderColor = "#1a7a73")}
        onBlur={(e) => (e.target.style.borderColor = fromDate ? "rgba(34, 197, 94, 0.8)" : "rgba(226, 232, 240, 0.8)")}
      />
    </div>

    <div style={{ minWidth: "150px" }}>
      <label
        style={{
          display: "block",
          marginBottom: "6px",
          fontSize: "12px",
          fontWeight: "500",
          color: "#64748b",
        }}
      >
        To Date
      </label>
      <input
        type="date"
        value={toDate ? toDate.toISOString().split('T')[0] : ""}
        onChange={(e) => {
          const selectedDate = e.target.value;
          const today = new Date().toISOString().split("T")[0];
          const selectedDateObj = selectedDate ? new Date(selectedDate) : null;

          // Check if selected date is in the future
          if (selectedDate > today) {
            // Reset to today's date and show warning
            setToDate(new Date(today));
            return;
          }

          // Check if toDate is before fromDate
          if (fromDate && selectedDateObj && selectedDateObj < fromDate) {
            // Don't update if invalid range
            return;
          }

          // Update the date
          setToDate(selectedDateObj);
        }}
        max={new Date().toISOString().split("T")[0]}
        min={fromDate ? fromDate.toISOString().split('T')[0] : ""}
        style={{
          width: "100%",
          padding: "12px 16px",
          borderRadius: "12px",
          border: toDate ? "1px solid rgba(34, 197, 94, 0.8)" : "1px solid rgba(226, 232, 240, 0.8)",
          background: "rgba(255, 255, 255, 0.9)",
          fontSize: "14px",
          outline: "none",
          transition: "all 0.3s ease",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
        }}
        onFocus={(e) => (e.target.style.borderColor = "#1a7a73")}
        onBlur={(e) => (e.target.style.borderColor = toDate ? "rgba(34, 197, 94, 0.8)" : "rgba(226, 232, 240, 0.8)")}
      />
    </div>

   
    <div style={{ minWidth: "100px" }}>
      <button
        onClick={() => {
          // Apply the date filter logic here
          console.log('Applying date filter:', { fromDate, toDate });
          
          // You can add your filter application logic here
          // For example, trigger a data refresh or update filtered results
          
          // Show a toast notification
          toast.success(`Date filter applied: ${fromDate.toLocaleDateString()} to ${toDate.toLocaleDateString()}`, {
            position: "top-center",
            autoClose: 3000,
          });
        }}
        disabled={!isDateFilterValid()}
        title={
          !fromDate || !toDate 
            ? "Please select both From Date and To Date" 
            : !isDateFilterValid() 
            ? "Please select valid date range" 
            : "Apply date filter"
        }
        style={{
          padding: "12px 16px",
          borderRadius: "12px",
          border: "none",
          background: isDateFilterValid() 
            ? "linear-gradient(145deg, #10b981, #059669)" 
            : "#d1d5db",
          color: isDateFilterValid() ? "white" : "#9ca3af",
          fontSize: "14px",
          fontWeight: "600",
          cursor: isDateFilterValid() ? "pointer" : "not-allowed",
          transition: "all 0.3s ease",
          boxShadow: isDateFilterValid() 
            ? "0 4px 12px rgba(16, 185, 129, 0.3)" 
            : "none",
          minHeight: "44px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          opacity: isDateFilterValid() ? 1 : 0.6,
        }}
        onMouseEnter={(e) => {
          if (isDateFilterValid()) {
            e.target.style.transform = "translateY(-1px)";
            e.target.style.boxShadow = "0 6px 16px rgba(16, 185, 129, 0.4)";
          }
        }}
        onMouseLeave={(e) => {
          if (isDateFilterValid()) {
            e.target.style.transform = "translateY(0)";
            e.target.style.boxShadow = "0 4px 12px rgba(16, 185, 129, 0.3)";
          }
        }}
      >
        Apply
      </button>
    </div>
  </div>
)} */}

                {/* Status - Only show if enabled in config */}
                {dashboardConfig.globalFilters.status && (
                  <div>
                    <select
                      style={{
                        padding: "12px 16px",
                        borderRadius: "12px",
                        border: "1px solid rgba(226, 232, 240, 0.8)",
                        background: "rgba(255, 255, 255, 0.9)",
                        fontSize: "14px",
                        minWidth: "160px",
                        outline: "none",
                      }}
                    >
                      <option>All Status</option>
                      <option>Active</option>
                      <option>Inactive</option>
                    </select>
                  </div>
                )}

                {/* Category - Only show if enabled in config */}
                {dashboardConfig.globalFilters.category && (
                  <div>
                    <select
                      style={{
                        padding: "12px 16px",
                        borderRadius: "12px",
                        border: "1px solid rgba(226, 232, 240, 0.8)",
                        background: "rgba(255, 255, 255, 0.9)",
                        fontSize: "14px",
                        minWidth: "160px",
                        outline: "none",
                      }}
                    >
                      <option>All Categories</option>
                      <option>Personal</option>
                      <option>Business</option>
                    </select>
                  </div>
                )}

                {/* Custom Filters */}
                {dashboardConfig.globalFilters.customFilters.map(
                  (filter, index) => (
                    <div key={index}>
                      <select
                        style={{
                          padding: "12px 16px",
                          borderRadius: "12px",
                          border: "1px solid rgba(226, 232, 240, 0.8)",
                          background: "rgba(255, 255, 255, 0.9)",
                          fontSize: "14px",
                          minWidth: "160px",
                          outline: "none",
                        }}
                      >
                        <option>{filter.name || `Filter ${index + 1}`}</option>
                        {filter.options.map((opt, i) => (
                          <option key={i}>{opt}</option>
                        ))}
                      </select>
                    </div>
                  )
                )}
              </div>

              {/* View Toggle */}
              {/* <div
              style={{
                display: "flex",
                background: "rgba(241, 245, 249, 0.8)",
                borderRadius: "12px",
                overflow: "hidden",
                border: "1px solid rgba(226, 232, 240, 0.6)",
                boxShadow: "inset 0 2px 4px rgba(0, 0, 0, 0.05)",
              }}
            >
              <button
                onClick={() => setGridView("grid")}
                title="Grid View"
                style={{
                  padding: "10px 16px",
                  background:
                    gridView === "grid"
                      ? "linear-gradient(145deg, #4f46e5, #6366f1)"
                      : "transparent",
                  color: gridView === "grid" ? "white" : "#64748b",
                  border: "none",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.3s ease",
                  borderRadius: "8px",
                  margin: "2px",
                }}
              >
                <LayoutGrid size={16} />
              </button>
              <button
                onClick={() => setGridView("list")}
                title="List View"
                style={{
                  padding: "10px 16px",
                  background:
                    gridView === "list"
                      ? "linear-gradient(145deg, #4f46e5, #6366f1)"
                      : "transparent",
                  color: gridView === "list" ? "white" : "#64748b",
                  border: "none",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.3s ease",
                  borderRadius: "8px",
                  margin: "2px",
                }}
              >
                <List size={16} />
              </button>
            </div> */}
            </div>
          </div>

          {/* Main Dashboard Grid */}
          <div style={{ zIndex: -1, position: "relative" }}>
            <GridLayoutCards
              items={filteredItems}
              handleEdit={handleEdit}
              handleDelete={handleDelete}
              handleDuplicate={handleDuplicate}
              isEditingLayout={isEditingLayout}
              onGridUpdate={(updatedItems) => {
                // Implement this function to update your state with the new positions/sizes
                // updateItemsLayout(updatedItems);
              }}
              renderIcon={renderIcon}
              handleRestore={handleRestore}
              tablePagination={tablePagination}
              onTablePageChange={handleTablePageChange}
              onTableRowsPerPageChange={handleTableRowsPerPageChange}
              uploadedIcons={uploadedIcons}
              iconSizes={iconSizes}
              isViewMode={isViewMode}
              searchTerm={searchTerm}
              originalItemsCount={dashboardItems.length}
              selectedBankId={selectedBankId}
              onBankSelect={handleBankSelect}
              selectedBranchId={selectedBranchId}
              onBranchSelect={handleBranchSelect}
              selectionData={selectionData}
              onClearSelections={handleClearSelections}
              getCurrentSelection={getCurrentSelection}
              selectedBankIds={selectedBankIds}
              selectedBranchIds={selectedBranchIds}
              handleMultiBankSelect={handleMultiBankSelect}
              handleMultiBranchSelect={handleMultiBranchSelect}
              attachmentList={attachmentList}
              savedQueries={savedQueries}
              selectedDefaultBankId={dashboardConfig.defaultBankId}
              instanceBankIds={instanceBankIds}
            />
          </div>
          {removedItems.length > 0 && (
            <div
              style={{
                marginTop: "40px",
                background: "rgba(255, 255, 255, 0.9)",
                backdropFilter: "blur(20px)",
                borderRadius: "16px",
                padding: "24px",
                border: "1px solid rgba(255, 255, 255, 0.3)",
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "8px",
                }}
              >
                <h2
                  style={{
                    fontSize: "18px",
                    fontWeight: "700",
                    color: "#1e293b",
                    margin: 0,
                  }}
                >
                  Removed Widgets ({removedItems.length})
                </h2>

                {/* Restore All Button */}
                <button
                  onClick={() => {
                    setDashboardItems((prev) => [...prev, ...removedItems]);
                    setRemovedItems([]);
                    setShowRemovedSection(false);
                  }}
                  style={{
                    padding: "8px 16px",
                    backgroundColor: "#10b981",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "500",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    transition: "background-color 0.2s",
                  }}
                  onMouseOver={(e) =>
                    (e.target.style.backgroundColor = "#059669")
                  }
                  onMouseOut={(e) =>
                    (e.target.style.backgroundColor = "#10b981")
                  }
                >
                  <RefreshCw size={16} />
                  Restore All
                </button>
              </div>

              <p
                style={{
                  color: "#64748b",
                  fontSize: "14px",
                  marginBottom: "20px",
                  fontWeight: "400",
                }}
              >
                Widgets below are temporarily removed. You can restore them
                individually or permanently delete them.
              </p>

              {/* Render Full Cards in Removed Section */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                  gap: "20px",
                  minHeight: "120px",
                  background: "rgba(248, 250, 252, 0.6)",
                  borderRadius: "12px",
                  padding: "20px",
                  border: "2px dashed rgba(203, 213, 225, 0.8)",
                  backdropFilter: "blur(10px)",
                }}
              >
                {removedItems.map((item, index) => (
                  <div
                    key={item.id}
                    style={{
                      position: "relative",
                      opacity: 0.8,
                      transform: "scale(0.95)",
                      transition: "all 0.3s ease",
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.opacity = "1";
                      e.currentTarget.style.transform = "scale(1)";
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.opacity = "0.8";
                      e.currentTarget.style.transform = "scale(0.95)";
                    }}
                  >
                    {/* Overlay with action buttons */}
                    <div
                      style={{
                        position: "absolute",
                        top: "8px",
                        right: "8px",
                        display: "flex",
                        gap: "8px",
                        zIndex: 10,
                      }}
                    >
                      {/* Restore Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRestore(item.id);
                        }}
                        style={{
                          padding: "6px 12px",
                          backgroundColor: "#10b981",
                          color: "white",
                          border: "none",
                          borderRadius: "6px",
                          cursor: "pointer",
                          fontSize: "12px",
                          fontWeight: "500",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                        }}
                        onMouseOver={(e) =>
                          (e.target.style.backgroundColor = "#059669")
                        }
                        onMouseOut={(e) =>
                          (e.target.style.backgroundColor = "#10b981")
                        }
                      >
                        <RefreshCw size={12} />
                        Restore
                      </button>

                      {/* Permanent Delete Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setItemToDelete(item);
                          setShowDeleteConfirmation(true);
                        }}
                        style={{
                          padding: "6px 12px",
                          backgroundColor: "#ef4444",
                          color: "white",
                          border: "none",
                          borderRadius: "6px",
                          cursor: "pointer",
                          fontSize: "12px",
                          fontWeight: "500",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                        }}
                        onMouseOver={(e) =>
                          (e.target.style.backgroundColor = "#dc2626")
                        }
                        onMouseOut={(e) =>
                          (e.target.style.backgroundColor = "#ef4444")
                        }
                      >
                        <Trash size={12} />
                        Delete
                      </button>
                    </div>

                    {/* Render the actual card component */}
                    <DraggableCard
                      item={item}
                      index={index}
                      moveItem={() => {}} // No need to move in removed section
                      handleEdit={() => {}} // Disable editing in removed section
                      handleDelete={(id) => {
                        setItemToDelete(item);
                        setShowDeleteConfirmation(true);
                      }}
                      renderIcon={renderIcon}
                      isRemoved={true}
                      handleRestore={handleRestore}
                      handleDuplicate={() => {}} // Disable duplication in removed section
                      isEditingLayout={false} // Disable layout editing in removed section
                      tablePage={tablePagination[item.id]?.page || 1}
                      tableRowsPerPage={
                        tablePagination[item.id]?.rowsPerPage || 5
                      }
                      onTablePageChange={(page) =>
                        handleTablePageChange(item.id, page)
                      }
                      onTableRowsPerPageChange={(rowsPerPage) =>
                        handleTableRowsPerPageChange(item.id, rowsPerPage)
                      }
                      uploadedIcons={uploadedIcons}
                      iconSizes={iconSizes}
                      isViewMode={true} // Set to view mode to prevent editing
                    />
                  </div>
                ))}
              </div>

              {/* Clear All Button */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: "20px",
                }}
              >
                <button
                  onClick={() => {
                    if (removedItems.length > 0) {
                      setItemToDelete({
                        id: "all",
                        title: `all ${removedItems.length} removed widgets`,
                      });
                      setShowDeleteConfirmation(true);
                    }
                  }}
                  style={{
                    padding: "10px 20px",
                    backgroundColor: "transparent",
                    color: "#ef4444",
                    border: "2px solid #ef4444",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "500",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    transition: "all 0.2s",
                  }}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = "#ef4444";
                    e.target.style.color = "white";
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = "transparent";
                    e.target.style.color = "#ef4444";
                  }}
                >
                  <Trash size={16} />
                  Clear All Removed Items
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Add/Edit Widget & Config Popup */}
      {showPopup && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            backdropFilter: "blur(4px)",
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              width: "100%",
              maxWidth: "600px",
              maxHeight: "80vh",
              borderRadius: "16px",
              boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Header */}
            <div
              style={{
                padding: "24px",
                borderBottom: "1px solid #e2e8f0",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "16px",
                }}
              >
                <h2
                  style={{
                    fontSize: "20px",
                    fontWeight: "700",
                    margin: 0,
                  }}
                >
                  {newItem.isEditing
                    ? "Edit Dashboard & Widget"
                    : "Configure Dashboard & Add Widget"}
                </h2>
                <button
                  onClick={handleClose}
                  style={{
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    border: "none",
                    borderRadius: "8px",
                    padding: "8px",
                    cursor: "pointer",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <X size={18} />
                </button>
              </div>

              {/* Step Progress */}
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                {Array.from({ length: totalSteps }, (_, i) => (
                  <React.Fragment key={i}>
                    <div
                      style={{
                        width: "32px",
                        height: "32px",
                        borderRadius: "50%",
                        backgroundColor:
                          i + 1 <= currentStep
                            ? "white"
                            : "rgba(255, 255, 255, 0.3)",
                        color: i + 1 <= currentStep ? "#667eea" : "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "14px",
                        fontWeight: "600",
                      }}
                    >
                      {i + 1}
                    </div>
                    {i < totalSteps - 1 && (
                      <div
                        style={{
                          width: "40px",
                          height: "2px",
                          backgroundColor:
                            i + 1 < currentStep
                              ? "white"
                              : "rgba(255, 255, 255, 0.3)",
                        }}
                      />
                    )}
                  </React.Fragment>
                ))}
              </div>

              <div
                style={{ marginTop: "12px", fontSize: "14px", opacity: 0.9 }}
              >
                Step {currentStep} of {totalSteps}:{" "}
                {currentStep === 1
                  ? "Dashboard Information"
                  : currentStep === 2
                  ? "Global Filters"
                  : currentStep === 3
                  ? "Widget Configuration"
                  : "Widget Configuration"}
              </div>
            </div>

            {/* Content */}
            <div style={{ flex: 1, padding: "24px", overflowY: "auto" }}>
              {/* Step 1: Dashboard Information */}
              {currentStep === 1 && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "20px",
                  }}
                >
                  {/* Banks Dropdown */}

                  {/* Dashboard Title */}
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#374151",
                      }}
                    >
                      Dashboard Title{" "}
                      <span style={{ color: "#ef4444" }}>*</span>
                    </label>
                    <input
                      type="text"
                      value={dashboardConfig.title}
                      onChange={(e) => {
                        setDashboardConfig((prev) => ({
                          ...prev,
                          title: e.target.value,
                        }));
                        // Clear error when user starts typing
                        if (e.target.value.trim() !== "") {
                          setDashboardFormErrors((prev) => ({
                            ...prev,
                            title: undefined,
                          }));
                        }
                      }}
                      placeholder="Enter dashboard title"
                      maxLength={60}
                      style={{
                        width: "100%",
                        padding: "12px 16px",
                        borderRadius: "8px",
                        border: "1px solid #d1d5db",
                        fontSize: "14px",
                        outline: "none",
                        transition: "border-color 0.2s",
                        "&:focus": { borderColor: "#667eea" },
                      }}
                    />
                    {/* Show validation error */}
                    {dashboardFormErrors.title && (
                      <div
                        style={{
                          color: "#ef4444",
                          fontSize: "12px",
                          marginTop: "4px",
                        }}
                      >
                        {dashboardFormErrors.title}
                      </div>
                    )}

                    <span
                      style={{
                        color: "#6b7280",
                        fontSize: "12px",
                        display: "block",
                        marginTop: "4px",
                        // marginLeft:"-28px",
                      }}
                    >
                      Character count: {dashboardConfig.title.length}/60
                    </span>
                  </div>
                  {/* District/Block Selection - Update this section */}
                  <div style={{ flex: 1 }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#374151",
                      }}
                    >
                      {userRole === "BRANCH OFFICER" ? (
                        <>
                          Block Name <span style={{ color: "#ef4444" }}>*</span>
                        </>
                      ) : roleName?.[0]?.is_admin === true ? (
                        ""
                      ) : (
                        <>
                          Select Banks{" "}
                          <span style={{ color: "#ef4444" }}>*</span>
                        </>
                      )}
                    </label>

                    {userRole === "BRANCH OFFICER" ? (
                      // Show disabled input with region_name for Branch Officer
                      <>
                        <input
                          type="text"
                          value={userRegionName}
                          disabled
                          style={{
                            width: "100%",
                            padding: "12px 16px",
                            borderRadius: "8px",
                            border: "1px solid #d1d5db",
                            fontSize: "14px",
                            backgroundColor: "#f9fafb",
                            color: "#6b7280",
                            cursor: "not-allowed",
                          }}
                        />
                        {userlist && (
                          <div style={{ marginTop: "12px" }}>
                            <label
                              style={{
                                display: "block",
                                marginBottom: "8px",
                                fontSize: "14px",
                                fontWeight: "600",
                                color: "#374151",
                              }}
                            >
                              Select User
                            </label>
                            <select
                              value={dashboardConfig.selectedUserId || ""}
                              onChange={(e) => {
                                setDashboardConfig((prev) => ({
                                  ...prev,
                                  selectedUserId: e.target.value,
                                }));
                              }}
                              style={{
                                width: "100%",
                                padding: "12px 16px",
                                borderRadius: "8px",
                                border: "1px solid #d1d5db",
                                fontSize: "14px",
                                outline: "none",
                                backgroundColor: "white",
                              }}
                            >
                              <option value="">Choose a user...</option>
                              {userlist.map((user) => (
                                <option key={user.id} value={user.id}>
                                  {`${user.first_name} ${user.last_name}`}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                      </>
                    ) : roleName?.[0]?.is_admin === true ? (
                      // Show Block Selection for Head Office Admin
                      <></>
                    ) : (
                      // Show Bank Selection for other roles
                      <Select
                        isMulti
                        options={[
                          { value: "all", label: "All Banks" },
                          ...bankList,
                        ]}
                        value={
                          dashboardConfig.selectedBankIds?.length
                            ? dashboardConfig.selectedBankIds
                                .filter((id) => id !== "all") // remove "all" from visible value
                                .map((id) => {
                                  const matchedBank = bankList.find(
                                    (b) => String(b.value) === String(id)
                                  );
                                  return matchedBank || null;
                                })
                                .filter(Boolean)
                            : [] // default empty
                        }
                        onChange={(selectedOptions) => {
                          const selectedValues = selectedOptions.map(
                            (opt) => opt.value
                          );

                          if (selectedValues.includes("all")) {
                            // If "All Banks" is selected, select all banks
                            const allBankIds = bankList.map((bank) =>
                              String(bank.value)
                            );
                            setDashboardConfig((prev) => ({
                              ...prev,
                              selectedBankIds: ["all", ...allBankIds],
                            }));
                          } else {
                            // Regular selection
                            setDashboardConfig((prev) => ({
                              ...prev,
                              selectedBankIds: selectedValues,
                            }));
                          }
                          if (selectedValues.length > 0) {
                            setDashboardFormErrors((prev) => ({
                              ...prev,
                              selectedBankIds: undefined,
                            }));
                          }
                        }}
                        styles={{
                          control: (base) => ({
                            ...base,
                            padding: "2px",
                            borderRadius: "8px",
                            border: "1px solid #d1d5db",
                            fontSize: "14px",
                            cursor: "pointer",
                          }),
                        }}
                        placeholder="Choose banks..."
                      />
                    )}
                    {dashboardFormErrors.selectedBankIds && (
                      <div
                        style={{
                          color: "red",
                          fontSize: "13px",
                          marginTop: "6px",
                        }}
                      >
                        {dashboardFormErrors.selectedBankIds}
                      </div>
                    )}
                  </div>
                  {/* Add this after the existing bank selection */}
                  {/* Default Bank Selection - Show when banks are selected */}
                  {dashboardConfig.selectedBankIds &&
                    dashboardConfig.selectedBankIds.length > 0 && (
                      <div style={{ flex: 1 }}>
                        <label
                          style={{
                            display: "block",
                            marginBottom: "8px",
                            fontSize: "14px",
                            fontWeight: "600",
                            color: "#374151",
                          }}
                        >
                          Default Bank{" "}
                          <span style={{ color: "#ef4444" }}>*</span>
                        </label>
                        <select
                          value={dashboardConfig.defaultBankId || ""}
                          onChange={(e) => {
                            const selectedValue =
                              e.target.value === "" ? null : e.target.value;
                            setDashboardConfig((prev) => ({
                              ...prev,
                              defaultBankId: selectedValue,
                            }));
                          }}
                          style={{
                            width: "100%",
                            padding: "12px 16px",
                            borderRadius: "8px",
                            border: "1px solid #d1d5db",
                            fontSize: "14px",
                            outline: "none",
                            backgroundColor: "white",
                          }}
                        >
                          <option value="">Choose default bank...</option>
                          {/* Show all banks when "all" is selected, otherwise show only selected banks */}
                          {(dashboardConfig.selectedBankIds?.includes("all")
                            ? bankList
                            : bankList.filter((bank) => {
                                const bankId = String(bank.value);
                                const selectedIds =
                                  dashboardConfig.selectedBankIds.map((id) =>
                                    String(id)
                                  );
                                return selectedIds.includes(bankId);
                              })
                          ).map((bank) => (
                            <option key={bank.value} value={bank.value}>
                              {bank.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                  {/* Description */}
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#374151",
                      }}
                    >
                      Description
                    </label>
                    <textarea
                      value={dashboardConfig.description}
                      onChange={(e) =>
                        setDashboardConfig((prev) => ({
                          ...prev,
                          description: e.target.value,
                        }))
                      }
                      maxLength={250}
                      placeholder="Enter dashboard description"
                      rows={3}
                      style={{
                        width: "100%",
                        padding: "12px 16px",
                        borderRadius: "8px",
                        border: "1px solid #d1d5db",
                        fontSize: "14px",
                        outline: "none",
                        resize: "vertical",
                        fontFamily: "inherit",
                      }}
                    />
                    <span style={{ color: "gray" }}>
                      Character count: {dashboardConfig.description.length}/250
                    </span>
                  </div>
                </div>
              )}
              {/* Step 2: Global Filters */}
              {currentStep === 2 && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "20px",
                  }}
                >
                  <div>
                    <h3
                      style={{
                        fontSize: "16px",
                        fontWeight: "600",
                        marginBottom: "16px",
                      }}
                    >
                      Common Filters
                    </h3>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "12px",
                      }}
                    >
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={dashboardConfig.globalFilters.search}
                          onChange={(e) =>
                            setDashboardConfig((prev) => ({
                              ...prev,
                              globalFilters: {
                                ...prev.globalFilters,
                                search: e.target.checked,
                              },
                            }))
                          }
                          style={{ width: "16px", height: "16px" }}
                        />
                        <Search size={16} color="#6b7280" />
                        <span style={{ fontSize: "14px" }}>Search Filter</span>
                      </label>

                      {/* <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={dashboardConfig.globalFilters.dateRange}
                          onChange={(e) =>
                            setDashboardConfig((prev) => ({
                              ...prev,
                              globalFilters: {
                                ...prev.globalFilters,
                                dateRange: e.target.checked,
                              },
                            }))
                          }
                          style={{ width: "16px", height: "16px" }}
                        />
                        <Calendar size={16} color="#6b7280" />
                        <span style={{ fontSize: "14px" }}>
                          Date Range Filter
                        </span>
                      </label> */}
                    </div>
                  </div>
                </div>
              )}
              {/* Step 3: Widget Configuration */}
              {currentStep === 3 && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px",
                  }}
                >
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "6px",
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#374151",
                      }}
                    >
                      Widget Category
                    </label>
                    <select
                      value={newItem.type}
                      onChange={(e) =>
                        setNewItem({ ...newItem, type: e.target.value })
                      }
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        borderRadius: "6px",
                        border: "1px solid #d1d5db",
                        fontSize: "14px",
                      }}
                    >
                      {[
                        { value: "card", label: "Card" },
                        { value: "chart", label: "Chart" },
                        { value: "table", label: "Table" },
                        { value: "map", label: "Map" },
                        {
                          value: "HO_bank_list",
                          label: "Bank and Branches List",
                        },
                      ]
                        .slice()
                        .sort((a, b) => a.label.localeCompare(b.label)) // sort alphabetically by label
                        .map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                    </select>
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "6px",
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#374151",
                      }}
                    >
                      Widget Title
                    </label>
                    <input
                      type="text"
                      value={newItem.title}
                      onChange={(e) =>
                        setNewItem({ ...newItem, title: e.target.value })
                      }
                      placeholder="Widget title"
                      maxLength={60}
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        borderRadius: "6px",
                        border: "1px solid #d1d5db",
                        fontSize: "14px",
                      }}
                    />
                    <span
                      style={{
                        color: "#6b7280",
                        fontSize: "12px",
                        display: "block",
                        marginTop: "4px",
                        marginLeft: "8px",
                      }}
                    >
                      Character count: {newItem.title.length}/60
                    </span>
                  </div>

                  {newItem.type === "card" && (
                    <>
                      <div></div>
                    </>
                  )}
                  {newItem.type === "chart" && (
                    <>
                      <div></div>
                    </>
                  )}
                  {newItem.type === "table" && (
                    <>
                      <div></div>
                    </>
                  )}
                  {newItem.type === "map" && (
                    <>
                      <div></div>
                    </>
                  )}
                </div>
              )}
            </div>
            {/* Footer */}
            <div
              style={{
                padding: "20px 24px",
                borderTop: "1px solid #e2e8f0",
                backgroundColor: "#f9fafb",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <button
                onClick={prevStep}
                disabled={currentStep === 1}
                style={{
                  padding: "10px 16px",
                  backgroundColor: currentStep === 1 ? "#e5e7eb" : "#6b7280",
                  color: currentStep === 1 ? "#9ca3af" : "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: currentStep === 1 ? "not-allowed" : "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <ChevronLeft size={16} />
                Previous
              </button>

              <div style={{ display: "flex", gap: "12px" }}>
                {/* Next button for steps 1-3 */}
                {/* Next button for steps 1-3 */}
                {currentStep < totalSteps && (
                  <button
                    onClick={() => {
                      if (currentStep === 1) {
                        // Validate before moving to next step
                        if (!validateDashboardStepOne()) return; // Don't proceed if validation fails
                        nextStep();
                      } else if (currentStep === 2) {
                        // ADDED: Handle step 2 navigation (Global Filters step)
                        // Add any step 2 validation if needed here
                        nextStep();
                      } else if (currentStep === 3) {
                        // ADDED: Handle step 3 navigation (Widget Configuration step)
                        // Add any step 3 validation if needed here
                        nextStep();
                      } else {
                        // Default case - just move to next step
                        nextStep();
                      }
                    }}
                    disabled={
                      currentStep === 1 &&
                      (!dashboardConfig.title ||
                        dashboardConfig.title.trim() === "" ||
                        (userRole === "SUPER ADMIN" &&
                          (!dashboardConfig.selectedBankIds ||
                            dashboardConfig.selectedBankIds.length === 0 ||
                            (dashboardConfig.selectedBankIds.length === 1 &&
                              dashboardConfig.selectedBankIds[0] === "") ||
                            // Include default bank validation for ALL cases (including "all")
                            !dashboardConfig.defaultBankId ||
                            dashboardConfig.defaultBankId === "")))
                      // ADDED: You can add step 2 and step 3 disabled conditions here if needed
                      // || (currentStep === 2 && /* your step 2 validation conditions */)
                      // || (currentStep === 3 && /* your step 3 validation conditions */)
                    }
                    style={{
                      padding: "10px 16px",
                      backgroundColor:
                        currentStep === 1 &&
                        (!dashboardConfig.title ||
                          dashboardConfig.title.trim() === "" ||
                          (userRole === "SUPER ADMIN" &&
                            (!dashboardConfig.selectedBankIds ||
                              dashboardConfig.selectedBankIds.length === 0 ||
                              (dashboardConfig.selectedBankIds.length === 1 &&
                                dashboardConfig.selectedBankIds[0] === "") ||
                              // Include default bank validation for ALL cases
                              !dashboardConfig.defaultBankId ||
                              dashboardConfig.defaultBankId === "")))
                          ? "#d1d5db"
                          : "#667eea",
                      color: "white",
                      border: "none",
                      borderRadius: "8px",
                      cursor:
                        currentStep === 1 &&
                        (!dashboardConfig.title ||
                          dashboardConfig.title.trim() === "" ||
                          (userRole === "SUPER ADMIN" &&
                            (!dashboardConfig.selectedBankIds ||
                              dashboardConfig.selectedBankIds.length === 0 ||
                              (dashboardConfig.selectedBankIds.length === 1 &&
                                dashboardConfig.selectedBankIds[0] === "") ||
                              // Include default bank validation
                              !dashboardConfig.defaultBankId ||
                              dashboardConfig.defaultBankId === "")))
                          ? "not-allowed"
                          : "pointer",
                      fontSize: "14px",
                      fontWeight: "500",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    {currentStep === 1 ? "Next" : "Next"}
                    <ChevronRight size={16} />
                  </button>
                )}

                {/* Final step button */}
                {currentStep === totalSteps && (
                  <button
                    onClick={() => {
                      if (!dashboardConfig.title.trim()) {
                        alert("Dashboard name is required");
                        return;
                      }
                      handleAddItem();
                      setShowPopup(false);
                    }}
                    style={{
                      padding: "10px 16px",
                      backgroundColor: "#10b981",
                      color: "white",
                      border: "none",
                      borderRadius: "8px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "600",
                    }}
                  >
                    <Save
                      size={16}
                      style={{ marginTop: "-3px", marginRight: "3px" }}
                    />
                    Save & Add Widget
                  </button>
                )}
                {editMode === "dashboard" && (
                  <button
                    onClick={() => {
                      if (!validateDashboardStepOne()) {
                        // Show specific error message
                        if (!dashboardConfig.title.trim()) {
                          toast.error("Dashboard title is required", {
                            position: "top-center",
                          });
                        }
                        if (
                          userRole === "SUPER ADMIN" &&
                          (!dashboardConfig.selectedBankIds ||
                            dashboardConfig.selectedBankIds.length === 0)
                        ) {
                          toast.error("Please select at least one Bank", {
                            position: "top-center",
                          });
                        }
                        return;
                      }
                      // Save dashboard configuration without adding widget
                      setDashboardName(dashboardConfig.title);
                      setDashboardDescription(dashboardConfig.description);

                      saveDashboardDetails(
                        dashboardConfig.title,
                        dashboardConfig.description
                      );
                      setShowPopup(false);
                      setCurrentStep(1);
                    }}
                    style={{
                      padding: "10px 16px",
                      backgroundColor: "#10b981",
                      color: "white",
                      border: "none",
                      borderRadius: "8px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "600",
                    }}
                  >
                    <Save
                      size={16}
                      style={{ marginTop: "-3px", marginRight: "3px" }}
                    />
                    Save Configuration
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Add/Edit Widget Popup */}
      {showEditPopup && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 50,
            backdropFilter: "blur(4px)",
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "32px",
              width: "100%",
              maxWidth:
                newItem.type === "table"
                  ? "55rem"
                  : newItem.type === "chart"
                  ? "53rem"
                  : "50rem",

              margin: "16px",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
              maxHeight: "550px",
              overflow: "auto",
              marginLeft: newItem.type === "table" ? "87px" : "0px",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "24px",
              }}
            >
              <h2
                style={{
                  fontSize: "24px",
                  fontWeight: "bold",
                  color: "#111827",
                }}
              >
                {newItem.isEditing ? "Edit Widget" : "Add New Widget"}
              </h2>
              <button
                onClick={() => {
                  setShowEditPopup(false);
                  resetNewItem();
                }}
                style={{
                  padding: "8px",
                  backgroundColor: "#f3f4f6",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  transition: "background-color 0.2s",
                }}
                onMouseOver={(e) =>
                  (e.target.style.backgroundColor = "#e5e7eb")
                }
                onMouseOut={(e) => (e.target.style.backgroundColor = "#f3f4f6")}
              >
                <X size={20} />
              </button>
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <div style={{ minWidth: "385px" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontSize: "14px",
                      fontWeight: "bold",
                      color: "#374151",
                    }}
                  >
                    Widget Category
                  </label>
                  <select
                    value={newItem.type}
                    onChange={(e) =>
                      setNewItem({ ...newItem, type: e.target.value })
                    }
                    disabled={newItem.isEditing}
                    style={{
                      width: newItem.type === "table" ? "103%" : "93%",
                      padding: "10px 12px",
                      borderRadius: "8px",
                      border: "1px solid #d1d5db",
                      backgroundColor: newItem.isEditing ? "#f9fafb" : "white",
                      fontSize: "14px",
                      color: newItem.isEditing ? "#6b7280" : "#374151",
                      outline: "none",
                      cursor: newItem.isEditing ? "not-allowed" : "pointer",
                      transition: "border-color 0.2s",
                    }}
                    onFocus={(e) => (e.target.style.borderColor = "#1a7a73")}
                    onBlur={(e) => (e.target.style.borderColor = "#d1d5db")}
                  >
                    <option value="card">Card</option>
                    <option value="chart">Chart</option>
                    <option value="table">Table</option>
                    <option value="map">Map</option>
                    <option value="HO_bank_list">Bank and Branches List</option>
                  </select>
                </div>

                <div style={{ flex: 1 }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontSize: "14px",
                      fontWeight: "bold",
                      color: "#374151",
                      marginLeft: newItem.type === "table" ? "12px" : "-23px",
                    }}
                  >
                    Widget Title
                  </label>

                  {/* Title Input */}
                  <input
                    type="text"
                    value={newItem.title}
                    onChange={(e) =>
                      setNewItem({ ...newItem, title: e.target.value })
                    }
                    placeholder="Enter widget title"
                    maxLength={60}
                    style={{
                      width: newItem.type === "table" ? "96%" : "107%",
                      padding: "10px 12px",
                      borderRadius: "8px",
                      border: "1px solid #d1d5db",
                      fontSize: "14px",
                      color: "#374151",
                      outline: "none",
                      transition: "border-color 0.2s",
                      marginLeft: newItem.type === "table" ? "12px" : "-28px",
                    }}
                    onFocus={(e) => (e.target.style.borderColor = "#1a7a73")}
                    onBlur={(e) => (e.target.style.borderColor = "#d1d5db")}
                  />
                </div>
              </div>

              {/* Title Styling Configuration - Moved to next row */}
              <div style={{ marginTop: "16px", marginBottom: "16px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "12px",
                    fontSize: "14px",
                    fontWeight: "bold",
                    color: "#374151",
                  }}
                >
                  Widget Title Styling
                </label>

                <div
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    padding: "12px",
                    backgroundColor: "#f9fafb",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      gap: "12px",
                      alignItems: "flex-start",
                      flexWrap: "wrap",
                    }}
                  >
                    {/* Color Selection */}
                    <div style={{ flex: "1 1 150px", minWidth: "120px" }}>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "6px",
                          fontSize: "12px",
                          fontWeight: "500",
                          color: "#6b7280",
                        }}
                      >
                        Text Color
                      </label>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        <input
                          type="color"
                          value={newItem.title_color || "#000000"}
                          onChange={(e) => {
                            const color = e.target.value;
                            setNewItem({
                              ...newItem,
                              title_color: color,
                            });
                          }}
                          style={{
                            width: "25px",
                            height: "25px",
                            border: "1px solid #d1d5db",
                            borderRadius: "4px",
                            cursor: "pointer",
                            backgroundColor: "transparent",
                          }}
                        />
                        <input
                          type="text"
                          value={newItem.title_color || "#000000"}
                          onChange={(e) => {
                            const color = e.target.value;
                            setNewItem({
                              ...newItem,
                              title_color: color,
                            });
                          }}
                          style={{
                            flex: 1,
                            padding: "4px 6px",
                            borderRadius: "4px",
                            border: "1px solid #d1d5db",
                            fontSize: "11px",
                            outline: "none",
                          }}
                        />
                      </div>
                    </div>

                    {/* Font Size Selection */}
                    <div style={{ flex: "1 1 150px", minWidth: "120px" }}>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "6px",
                          fontSize: "12px",
                          fontWeight: "500",
                          color: "#6b7280",
                        }}
                      >
                        Font Size
                      </label>
                      <div
                        style={{
                          display: "flex",
                          gap: "6px",
                          alignItems: "center",
                        }}
                      >
                        <select
                          value={
                            newItem.fontSizeMode ||
                            (() => {
                              // Determine initial mode based on current fontSize value
                              const size = newItem.title_fontSize || 16;
                              if (size <= 12) return "small";
                              if (size <= 16) return "medium";
                              if (size <= 20) return "large";
                              if (size <= 24) return "extra-large";
                              return "custom";
                            })()
                          }
                          onChange={(e) => {
                            const option = e.target.value;
                            let newSize;

                            switch (option) {
                              case "small":
                                newSize = 12;
                                break;
                              case "medium":
                                newSize = 16;
                                break;
                              case "large":
                                newSize = 20;
                                break;
                              case "extra-large":
                                newSize = 24;
                                break;
                              case "custom":
                                // Keep current value or set to a default custom value
                                newSize = newItem.title_fontSize || 18;
                                break;
                              default:
                                newSize = 16;
                            }

                            setNewItem((prev) => ({
                              ...prev,
                              title_fontSize: newSize,
                              fontSizeMode: option,
                            }));
                          }}
                          style={{
                            width: "90px",
                            padding: "4px 6px",
                            borderRadius: "4px",
                            border: "1px solid #d1d5db",
                            fontSize: "11px",
                            outline: "none",
                          }}
                        >
                          <option value="small">Small</option>
                          <option value="medium">Medium</option>
                          <option value="large">Large</option>
                          <option value="extra-large">X-Large</option>
                          <option value="custom">Custom</option>
                        </select>

                        {/* Show custom input when mode is custom */}
                        {(newItem.fontSizeMode === "custom" ||
                          (!newItem.fontSizeMode &&
                            newItem.title_fontSize &&
                            ![12, 16, 20, 24].includes(
                              newItem.title_fontSize
                            ))) && (
                          <input
                            type="number"
                            value={newItem.title_fontSize || 16}
                            onChange={(e) => {
                              const value = parseInt(e.target.value);
                              if (!isNaN(value) && value >= 8 && value <= 72) {
                                setNewItem((prev) => ({
                                  ...prev,
                                  title_fontSize: value,
                                  fontSizeMode: "custom",
                                }));
                              }
                            }}
                            placeholder="px"
                            min="8"
                            max="72"
                            style={{
                              width: "50px",
                              padding: "4px 6px",
                              borderRadius: "4px",
                              border: "1px solid #d1d5db",
                              fontSize: "11px",
                              outline: "none",
                            }}
                          />
                        )}
                      </div>
                    </div>

                    {/* Text Alignment Selection */}
                    <div style={{ flex: "1 1 150px", minWidth: "120px" }}>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "6px",
                          fontSize: "12px",
                          fontWeight: "500",
                          color: "#6b7280",
                        }}
                      >
                        Text Alignment
                      </label>
                      <div
                        style={{
                          display: "flex",
                          gap: "4px",
                          border: "1px solid #d1d5db",
                          borderRadius: "4px",
                          padding: "2px",
                          backgroundColor: "white",
                        }}
                      >
                        {[
                          { value: "left", label: "L" },
                          { value: "center", label: "C" },
                          { value: "right", label: "R" },
                        ].map((alignment) => (
                          <button
                            key={alignment.value}
                            type="button"
                            onClick={() => {
                              setNewItem({
                                ...newItem,
                                title_textAlignment: alignment.value,
                              });
                            }}
                            style={{
                              flex: 1,
                              padding: "4px 2px",
                              borderRadius: "2px",
                              border: "none",
                              backgroundColor:
                                newItem.title_textAlignment === alignment.value
                                  ? "#1a7a73"
                                  : "transparent",
                              color:
                                newItem.title_textAlignment === alignment.value
                                  ? "white"
                                  : "#6b7280",
                              cursor: "pointer",
                              fontSize: "10px",
                              fontWeight: "500",
                              transition: "all 0.2s",
                            }}
                            onMouseOver={(e) => {
                              if (
                                newItem.title_textAlignment !== alignment.value
                              ) {
                                e.currentTarget.style.backgroundColor =
                                  "#f3f4f6";
                              }
                            }}
                            onMouseOut={(e) => {
                              if (
                                newItem.title_textAlignment !== alignment.value
                              ) {
                                e.currentTarget.style.backgroundColor =
                                  "transparent";
                              }
                            }}
                          >
                            {alignment.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {newItem.type === "card" && (
                <CardEditPopup
                  newItem={newItem}
                  setNewItem={setNewItem}
                  savedQueries={savedQueries}
                  setLoader={setLoader}
                  getData={getData}
                  getThemeURL={getThemeURL}
                  handleDateFieldsUpdate={handleDateFieldsUpdate}
                  selectedBankIds={dashboardConfig.selectedBankIds}
                  selectedDefaultBankId={dashboardConfig.defaultBankId}
                />
              )}

              {newItem.type === "HO_bank_list" && (
                <TreeListEditPopup
                  newItem={newItem}
                  setNewItem={setNewItem}
                  savedQueries={savedQueries}
                  setLoader={setLoader}
                  getData={getData}
                  getThemeURL={getThemeURL}
                  selectedBankIds={dashboardConfig.selectedBankIds}
                />
              )}
              {newItem.type === "chart" && (
                <ChartEditPopUp
                  newItem={newItem}
                  setNewItem={setNewItem}
                  chartTypes={chartTypes}
                  formatChartTypeName={formatChartTypeName}
                  savedQueries={savedQueries}
                  xAxisOptions={xAxisOptions}
                  yAxisOptions={yAxisOptions}
                  setXAxisOptions={setXAxisOptions}
                  setYAxisOptions={setYAxisOptions}
                  chartColors={chartColors}
                  setLoader={setLoader}
                  getThemeURL={getThemeURL}
                  getData={getData}
                  handleYAxisKeyChange={handleYAxisKeyChange}
                  handleYAxisLabelChange={handleYAxisLabelChange}
                  handleRemoveYAxisKey={handleRemoveYAxisKey}
                  handleAddYAxisKey={handleAddYAxisKey}
                  labelStyle={labelStyle}
                  dropdownStyle={dropdownStyle}
                  xAxisOrientationOptions={xAxisOrientationOptions}
                  handleDateFieldsUpdate={handleDateFieldsUpdate}
                  selectedBankIds={dashboardConfig.selectedBankIds}
                  selectedDefaultBankId={dashboardConfig.defaultBankId}
                />
              )}
              {newItem.type === "table" && (
                <TableEditForm
                  newItem={newItem}
                  setNewItem={setNewItem}
                  savedQueries={savedQueries}
                  setLoader={setLoader}
                  getData={getData}
                  getThemeURL={getThemeURL}
                  setDataSourcesForTable={setDataSourcesForTable}
                  handleDateFieldsUpdate={handleDateFieldsUpdate}
                  selectedBankIds={dashboardConfig.selectedBankIds}
                  selectedDefaultBankId={dashboardConfig.defaultBankId}
                  userRole={userRole}
                />
              )}
              {newItem.type === "map" && (
                <MapEdit
                  newItem={newItem}
                  setNewItem={setNewItem}
                  savedQueries={savedQueries}
                  dataSourcesForMap={dataSourcesForMap}
                  setDataSourcesForMap={setDataSourcesForMap}
                  uploadedIcons={uploadedIcons}
                  setUploadedIcons={setUploadedIcons}
                  iconSizes={iconSizes}
                  setIconSizes={setIconSizes}
                  iconMap={iconMap}
                  setLoader={setLoader}
                  labelStyle={labelStyle}
                  handleIconUpload={handleIconUpload}
                  attachmentList={attachmentList}
                  setAttachmentList={setAttachmentList}
                  handleDateFieldsUpdate={handleDateFieldsUpdate}
                  selectedBankIds={dashboardConfig.selectedBankIds}
                  selectedDefaultBankId={dashboardConfig.defaultBankId}
                />
              )}
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "12px",
                  marginTop: "24px",
                }}
              >
                <button
                  type="button"
                  onClick={() => setShowEditPopup(false)}
                  style={{
                    padding: "10px 20px",
                    backgroundColor: "#f3f4f6",
                    color: "#374151",
                    border: "none",
                    borderRadius: "8px",
                    fontWeight: "500",
                    cursor: "pointer",
                    transition: "background-color 0.2s",
                  }}
                  onMouseOver={(e) =>
                    (e.target.style.backgroundColor = "#e5e7eb")
                  }
                  onMouseOut={(e) =>
                    (e.target.style.backgroundColor = "#f3f4f6")
                  }
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (newItem.type === "table" && !newItem.dataSource) {
                      alert("Data source is required for table widgets");
                      return;
                    }
                    handleAddItem();
                    setShowEditPopup(false); // Also close the popup
                  }}
                  disabled={isDisabled}
                  style={{
                    padding: "10px 20px",
                    backgroundColor: isDisabled ? "#d1d5db" : "#1a7a73",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    fontWeight: "500",
                    cursor: isDisabled ? "not-allowed" : "pointer",
                    transition: "background-color 0.2s",
                  }}
                >
                  {newItem.isEditing ? "Update Widget" : "Add Widget"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            backdropFilter: "blur(4px)",
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              width: "100%",
              maxWidth: "400px",
              borderRadius: "16px",
              boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Header */}
            <div
              style={{
                padding: "24px",
                borderBottom: "1px solid #e2e8f0",
                // background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                background: "#FFC0CB",
                color: "white",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <h2
                  style={{
                    fontSize: "18px",
                    fontWeight: "700",
                    margin: 0,
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <AlertTriangle size={20} />
                  Confirm Permanent Delete
                </h2>
                <button
                  onClick={() => {
                    setShowDeleteConfirmation(false);
                    setItemToDelete(null);
                  }}
                  style={{
                    backgroundColor: "#EF4444",
                    border: "none",
                    borderRadius: "8px",
                    padding: "8px",
                    cursor: "pointer",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Content */}
            <div style={{ padding: "24px" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  marginBottom: "16px",
                }}
              >
                <div
                  style={{
                    backgroundColor: "#fef2f2",
                    padding: "12px",
                    borderRadius: "50%",
                  }}
                >
                  <Trash size={24} color="#ef4444" />
                </div>
                <div>
                  <h3
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#111827",
                      margin: 0,
                    }}
                  >
                    Delete "{itemToDelete?.title}" permanently?
                  </h3>
                  <p
                    style={{
                      fontSize: "14px",
                      color: "#6b7280",
                      margin: "4px 0 0 0",
                    }}
                  >
                    This action cannot be undone.
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  justifyContent: "space-between",
                }}
              >
                <button
                  onClick={() => {
                    setShowDeleteConfirmation(false);
                    setItemToDelete(null);
                  }}
                  style={{
                    padding: "10px 20px",
                    backgroundColor: "gray",
                    color: "#000000",
                    border: "none",
                    borderRadius: "8px",
                    fontWeight: "500",
                    cursor: "pointer",
                    fontSize: "14px",
                    transition: "background-color 0.2s",
                  }}
                  onMouseOver={(e) =>
                    (e.target.style.backgroundColor = "#e5e7eb")
                  }
                  onMouseOut={(e) =>
                    (e.target.style.backgroundColor = "#f3f4f6")
                  }
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (itemToDelete) {
                      handlePermanentDelete(itemToDelete.id);
                    }
                    setShowDeleteConfirmation(false);
                    setItemToDelete(null);
                  }}
                  style={{
                    padding: "10px 20px",
                    backgroundColor: "#ef4444",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    fontWeight: "500",
                    cursor: "pointer",
                    fontSize: "14px",
                    transition: "background-color 0.2s",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                  onMouseOver={(e) =>
                    (e.target.style.backgroundColor = "#dc2626")
                  }
                  onMouseOut={(e) =>
                    (e.target.style.backgroundColor = "#ef4444")
                  }
                >
                  <Trash size={16} />
                  Delete Permanently
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DndProvider>
  );
};

export default CreateNewDashboardForApi;
